/* eslint-disable */
// ============================================================
//  SCREENS AUTH - Splash, Lang, Legal, Auth, Login, Register
// ============================================================

import { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { PrimaryButton } from "./theme";
import { supabase, supabaseResetSender, trackSignupEvent, getAppSettings } from "./supabase";
import {
  T,
  loadLang,
  LANGS,
  LOGO,
  USER_CATS,
  USER_CATS_I18N,
  PRO_METIERS,
  ADMIN_EMAIL,
} from "./constants";
import { useLang, validatePassword, countryCode } from "./utils";
import { resendSignupCode, resendErrorText } from "./captcha-resend";
import IdentityPicker from "./IdentityPicker";
import { signInWithGoogle } from "./native-google";
import {
  G,
  CIcon,
  Btn,
  Field,
  Chk,
  Header,
  PasswordStrength,
} from "./ui";

// === SplashScreen ===
// === LandingPage ===
// Page d'accueil affichee aux nouveaux visiteurs (vue une seule fois)
export function LandingPage({onEnter, showToast}) {
  // Detection de la langue navigateur par defaut
  const detectBrowserLang = () => {
    try {
      const browserLang = (navigator.language || "en").substring(0, 2).toLowerCase();
      if (["fr","en","de","it","es","pt","nl"].includes(browserLang)) {
        return browserLang;
      }
    } catch(e){}
    return "en";
  };
  
  // Lire la langue depuis localStorage ou detecter
  const getInitialLang = () => {
    try {
      const saved = localStorage.getItem("hc_lang");
      if (saved && ["fr","en","de","it","es","pt","nl"].includes(saved)) return saved;
    } catch(e){}
    return detectBrowserLang();
  };
  
  const [currentLang, setCurrentLang] = useState(getInitialLang());
  
  // Recuperer les textes traduits
  const t = (T[currentLang] && T[currentLang].landing) || T.fr.landing;

  // ⚠️ Cet écran tient son PROPRE état de langue, distinct de celui d'App.jsx et
  // de celui de landing-shared. Depuis que les traductions sont chargées à la
  // demande (29 août 2026), il faut donc les charger ici aussi, sinon le
  // sélecteur de langue afficherait du français en silence.
  const [, setI18nTick] = useState(0);
  useEffect(() => {
    let vivant = true;
    loadLang(currentLang).finally(() => { if (vivant) setI18nTick(n => n + 1); });
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Changer langue — on charge AVANT de poser la langue, sinon l'affichage
  // passerait par le repli français puis rebasculerait.
  const changeLang = (code) => {
    loadLang(code).finally(() => setCurrentLang(code));
    try { localStorage.setItem("hc_lang", code); } catch(e){}
  };
  
  // Liste des langues disponibles
  const languages = [
    {code:"fr", flag:"🇫🇷", name:"Français"},
    {code:"en", flag:"🇬🇧", name:"English"},
    {code:"de", flag:"🇩🇪", name:"Deutsch"},
    {code:"it", flag:"🇮🇹", name:"Italiano"},
    {code:"es", flag:"🇪🇸", name:"Español"},
    {code:"pt", flag:"🇵🇹", name:"Português"},
    {code:"nl", flag:"🇳🇱", name:"Nederlands"}
  ];
  
  const [showLangPicker, setShowLangPicker] = useState(false);
  const currentLangObj = languages.find(l => l.code === currentLang) || languages[0];
  
  return (
    <div style={{
      minHeight:"100vh",
      background:"linear-gradient(180deg, #fff5f5 0%, #ffffff 50%, #fff5f5 100%)",
      display:"flex",
      flexDirection:"column",
      fontFamily:"var(--fb)"
    }}>
      {/* Header avec selecteur de langue */}
      <div style={{
        position:"sticky",
        top:0,
        zIndex:100,
        background:"rgba(255,255,255,.95)",
        backdropFilter:"blur(10px)",
        borderBottom:"1px solid var(--border)",
        padding:"12px 16px",
        display:"flex",
        justifyContent:"flex-end",
        alignItems:"center"
      }}>
        <div style={{position:"relative"}}>
          <button 
            onClick={()=>setShowLangPicker(!showLangPicker)}
            style={{
              background:"#fff",
              border:"1px solid var(--border)",
              borderRadius:8,
              padding:"6px 12px",
              fontSize:13,
              fontWeight:600,
              color:"var(--text)",
              cursor:"pointer",
              display:"flex",
              alignItems:"center",
              gap:6
            }}
          >
            <span style={{fontSize:16}}>{currentLangObj.flag}</span>
            <span>{currentLangObj.name}</span>
            <span style={{fontSize:10,marginLeft:4}}>{showLangPicker?"▲":"▼"}</span>
          </button>
          {showLangPicker && (
            <div style={{
              position:"absolute",
              top:"calc(100% + 6px)",
              right:0,
              background:"#fff",
              border:"1px solid var(--border)",
              borderRadius:10,
              boxShadow:"0 4px 20px rgba(0,0,0,.1)",
              padding:6,
              minWidth:160,
              zIndex:101
            }}>
              {languages.map(l => (
                <button
                  key={l.code}
                  onClick={()=>{changeLang(l.code);setShowLangPicker(false);}}
                  style={{
                    display:"flex",
                    alignItems:"center",
                    gap:10,
                    width:"100%",
                    padding:"8px 12px",
                    background:l.code===currentLang?"rgba(200,0,26,.06)":"transparent",
                    border:"none",
                    borderRadius:6,
                    cursor:"pointer",
                    fontSize:13,
                    color:"var(--text)",
                    fontWeight:l.code===currentLang?700:500
                  }}
                >
                  <span style={{fontSize:18}}>{l.flag}</span>
                  <span>{l.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Contenu principal */}
      <div style={{
        flex:1,
        display:"flex",
        flexDirection:"column",
        alignItems:"center",
        justifyContent:"center",
        padding:"30px 20px",
        maxWidth:430,
        margin:"0 auto",
        width:"100%"
      }}>
        
        {/* Logo */}
        <div style={{
          width:120,
          height:120,
          borderRadius:24,
          background:"linear-gradient(135deg,#C8001A 0%,#8B0011 100%)",
          display:"flex",
          alignItems:"center",
          justifyContent:"center",
          marginBottom:24,
          boxShadow:"0 8px 30px rgba(200,0,26,.25)",
          overflow:"hidden"
        }}>
          <img 
            src="https://horseconnex.com/icon-512.png" 
            alt="HorseConnex" 
            style={{width:"100%",height:"100%",objectFit:"contain"}}
            onError={(e)=>{e.target.style.display="none";}}
          />
        </div>
        
        {/* Nom HorseConnex */}
        <h1 style={{
          fontSize:32,
          fontWeight:900,
          color:"#1a1a1a",
          margin:"0 0 12px",
          fontFamily:"var(--fh)",
          letterSpacing:-0.5,
          textAlign:"center"
        }}>
          HorseConnex
        </h1>
        
        {/* Slogan */}
        <p style={{
          fontSize:18,
          fontWeight:700,
          color:"var(--red)",
          margin:"0 0 8px",
          textAlign:"center",
          lineHeight:1.3,
          maxWidth:340
        }}>
          {t.slogan}
        </p>
        
        {/* Made in Switzerland */}
        <p style={{
          fontSize:12,
          color:"var(--muted)",
          margin:"0 0 30px",
          textAlign:"center",
          letterSpacing:1.5,
          textTransform:"uppercase",
          fontWeight:600
        }}>
          {t.madeIn}
        </p>
        
        {/* Description emotionnelle */}
        <div style={{
          width:"100%",
          maxWidth:360,
          background:"#fff",
          borderRadius:16,
          padding:"24px 22px",
          boxShadow:"0 4px 20px rgba(0,0,0,.05)",
          border:"1px solid var(--border)",
          marginBottom:30
        }}>
          {t.description.split("\n\n").map((paragraph, i) => (
            <p key={i} style={{
              fontSize:14.5,
              color:"#333",
              lineHeight:1.7,
              margin: i === 0 ? "0 0 14px" : "0 0 14px",
              fontWeight: i === 0 ? 600 : 400,
              textAlign:"center"
            }}>
              {paragraph}
            </p>
          ))}
        </div>
        
        {/* Bouton CTA */}
        <button
          onClick={onEnter}
          style={{
            width:"100%",
            maxWidth:360,
            padding:"16px 24px",
            background:"linear-gradient(135deg,#C8001A 0%,#8B0011 100%)",
            color:"#fff",
            border:"none",
            borderRadius:14,
            fontSize:16,
            fontWeight:700,
            cursor:"pointer",
            boxShadow:"0 6px 20px rgba(200,0,26,.35)",
            transition:"all .2s",
            marginBottom:20
          }}
          onMouseDown={(e)=>e.currentTarget.style.transform="scale(.98)"}
          onMouseUp={(e)=>e.currentTarget.style.transform="scale(1)"}
          onMouseLeave={(e)=>e.currentTarget.style.transform="scale(1)"}
        >
          {t.cta}
        </button>

      </div>
      
      {/* Footer */}
      <div style={{
        padding:"20px",
        textAlign:"center",
        fontSize:10,
        color:"var(--muted)",
        borderTop:"1px solid var(--border)",
        background:"#fff"
      }}>
        © 2026 HorseConnex • horseconnex.com
      </div>
    </div>
  );
}

// === ComingSoonScreen ===
export function ComingSoonScreen({onAccess, showToast}) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const submit = async () => {
    if (!email || !email.includes("@")) {
      if (showToast) showToast({type:"error", text:"Veuillez entrer un email valide"});
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.from("early_signups").insert({ email: email.trim().toLowerCase() });
      if (error && error.code !== "23505") {
        if (showToast) showToast({type:"error", text:"Erreur: " + error.message});
      } else {
        setSuccess(true);
      }
    } catch(e) {
      if (showToast) showToast({type:"error", text:t.errConnection});
    }
    setSubmitting(false);
  };
  
  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg, #1A1A2E 0%, #2D1B4E 50%, #C8001A 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{maxWidth:430,width:"100%",textAlign:"center",color:"#fff"}}>
        <div style={{marginBottom:6}}>
          <img src={LOGO} alt="HorseConnex" style={{width:280,height:280,objectFit:"contain",filter:"drop-shadow(0 8px 24px rgba(0,0,0,.4))"}}/>
        </div>
        <div style={{fontSize:11,color:"rgba(255,255,255,.75)",letterSpacing:3,marginBottom:22,textTransform:"uppercase",marginTop:12}}>
          Connecting the equestrian world
        </div>
        <div style={{display:"inline-block",background:"rgba(255,255,255,.18)",padding:"8px 20px",borderRadius:30,fontSize:13,fontWeight:700,letterSpacing:2,marginBottom:28,border:"1px solid rgba(255,255,255,.3)"}}>
          ✨ COMING SOON 2026 ✨
        </div>
        <div style={{fontSize:15,lineHeight:1.6,marginBottom:24,color:"rgba(255,255,255,.95)",padding:"0 6px"}}>
          La plateforme mondiale qui rassemble<br/>tous les passionnes du monde equestre.
        </div>
        <div style={{fontSize:13,color:"rgba(255,255,255,.85)",marginBottom:6,fontWeight:600,letterSpacing:.5}}>
          Cavaliers · Ecuries · Marketplace · Communaute
        </div>
        <div style={{fontSize:12,color:"rgba(255,255,255,.7)",marginBottom:28,fontStyle:"italic"}}>
          et plus encore...
        </div>
        {!success ? (
          <div style={{background:"rgba(255,255,255,.97)",padding:"22px 18px",borderRadius:16,marginBottom:22,boxShadow:"0 8px 32px rgba(0,0,0,.25)"}}>
            <div style={{fontSize:13,fontWeight:700,color:"#1A1A2E",marginBottom:14,fontFamily:"var(--fh)"}}>
              🎉 Soyez prevenu du lancement !
            </div>
            <input type="email" placeholder="votre@email.com" value={email} onChange={e=>setEmail(e.target.value)} disabled={submitting} style={{width:"100%",padding:"12px 14px",border:"1.5px solid #ddd",borderRadius:10,fontSize:14,marginBottom:12,boxSizing:"border-box",outline:"none",color:"#333"}}/>
            <button onClick={submit} disabled={submitting} style={{width:"100%",padding:"13px",background:submitting?"#999":"#C8001A",color:"#fff",border:"none",borderRadius:10,fontSize:13,fontWeight:700,fontFamily:"var(--fh)",cursor:submitting?"wait":"pointer",letterSpacing:1.2}}>
              {submitting ? "ENVOI..." : "ME PREVENIR DU LANCEMENT"}
            </button>
            <div style={{fontSize:10,color:"#888",marginTop:10,lineHeight:1.4}}>
              Vos donnees sont protegees et ne seront utilisees<br/>que pour vous prevenir du lancement.
            </div>
          </div>
        ) : (
          <div style={{background:"rgba(34,197,94,.2)",border:"2px solid #22c55e",padding:"22px 18px",borderRadius:16,marginBottom:22}}>
            <div style={{fontSize:34,marginBottom:6}}>✅</div>
            <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>Inscription confirmee !</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,.9)"}}>Vous serez parmi les premiers prevenus du lancement.</div>
          </div>
        )}
        <div style={{fontSize:11,color:"rgba(255,255,255,.7)",letterSpacing:2,marginTop:12}}>
          🇨🇭 MADE IN SWITZERLAND
        </div>
        <div style={{fontSize:10,color:"rgba(255,255,255,.45)",marginTop:8}}>
          © 2026 HorseConnex. Tous droits reserves.
        </div>
      </div>
    </div>
  );
}

export function SplashScreen({onDone, lang="fr"}) {
  const t = useLang(lang);
  const [out,setOut]=useState(false);
  useEffect(()=>{
    const t1=setTimeout(()=>setOut(true),1900);
    const t2=setTimeout(onDone,2400);
    return()=>{clearTimeout(t1);clearTimeout(t2);};
  },[]);
  return (
    <div style={{minHeight:"100vh",background:"var(--red)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",opacity:out?0:1,transition:"opacity .5s ease"}}>
      <G/>
      <div className="fu" style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10}}>
        <img src={LOGO} alt="" style={{width:150,height:150,objectFit:"contain",filter:"brightness(0) invert(1)"}}/>
        <div style={{fontFamily:"var(--fh)",fontSize:11,fontWeight:700,letterSpacing:5,color:"rgba(255,255,255,.65)",textTransform:"uppercase"}}>{t.tagline}</div>
        <div style={{display:"flex",gap:6,marginTop:30}}>
          {[0,1,2].map(i=><div key={i} style={{width:7,height:7,borderRadius:"50%",background:"rgba(255,255,255,.6)",animation:`pls 1.3s ${i*.22}s infinite`}}/>)}
        </div>
      </div>
    </div>
  );
}

// === LangScreen ===
export function LangScreen({onSelect}) {
  return (
    <div style={{minHeight:"100vh",background:"var(--dark2)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"28px 24px"}}>
      <G/>
      <div className="fu" style={{textAlign:"center",marginBottom:32}}>
        <img src={LOGO} alt="" style={{width:88,height:88,objectFit:"contain"}}/>
        <div style={{fontFamily:"var(--fh)",fontSize:10,letterSpacing:4,color:"rgba(255,255,255,.3)",textTransform:"uppercase",marginTop:8}}>Choisissez votre langue</div>
      </div>
      <div className="fu1" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9,width:"100%"}}>
        {LANGS.map(l=>(
          <button key={l.code} onClick={()=>onSelect(l.code)} style={{display:"flex",alignItems:"center",gap:10,background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.1)",borderRadius:12,padding:"13px 14px",color:"#fff",fontSize:14,fontFamily:"var(--fb)",fontWeight:500,cursor:"pointer",transition:"background .15s"}}
            onMouseOver={e=>e.currentTarget.style.background="rgba(200,0,26,.25)"}
            onMouseOut={e=>e.currentTarget.style.background="rgba(255,255,255,.05)"}
          >
            <span style={{fontSize:22}}>{l.flag}</span>{l.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Pages légales : version traduite quand elle existe (nl pour l'instant), sinon français.
const legalUrl = (doc, lang) => `/${doc}${lang === "nl" ? ".nl" : ""}.html`;

// === LegalScreen ===
export function LegalScreen({onAccept}) {
  const [ok,setOk]=useState(false);
  const [cookiesOk,setCookiesOk]=useState(false);
  const [doc,setDoc]=useState(null);
  const docs = {
    "Conditions générales d'utilisation": "CONDITIONS GÉNÉRALES D'UTILISATION — HorseConnex©\nVersion 3.9 — Septembre 2026\n\n1. OBJET ET ACCEPTATION\nHorseConnex© est édité par Mickaël Carron, personne physique domiciliée en Suisse. En créant un compte, l'utilisateur accepte sans réserve les présentes CGU et notre Politique de Confidentialité. Si vous n'acceptez pas ces conditions, n'utilisez pas la Plateforme.\n\n2. DESCRIPTION DU SERVICE\nHorseConnex est une plateforme mondiale de mise en relation entre tous les acteurs du monde équestre : cavaliers, propriétaires d'écurie, vétérinaires, professeurs, marchands, parents, et toute personne liée au milieu équestre. La Plateforme propose : annonces (chevaux, écuries, cours, emplois, soins, stages, transport, élevage), annonces de recherche, cours pour enfants/poney, fiches écurie avec disponibilité des boxes, marketplace matériel, messagerie privée, géolocalisation des annonces sur carte, système d'avis entre utilisateurs, randonnées équestres et itinéraires cartographiés (tracés indicatifs), carnet de suivi du cheval, et annuaire de professionnels et de gîtes équestres.\n\n3. ACCEPTATION DES UTILISATEURS\n- Âge minimum : 13 ans. Les mineurs (moins de 18 ans) doivent utiliser la Plateforme sous la responsabilité et avec le consentement d'un parent ou tuteur, en particulier pour la messagerie, l'échange de coordonnées, les rencontres en personne et les sorties/randonnées.\n- Pour les mineurs : utilisation interdite sans consentement parental explicite.\n- Email valide obligatoire, vérifié par lien de confirmation.\n- L'utilisateur est responsable de la confidentialité de ses identifiants.\n- Mot de passe sécurisé exigé (8 caractères min, majuscule, minuscule, chiffre, caractère spécial).\n\n4. ABSENCE DE TRANSACTION FINANCIÈRE\nHorseConnex est EXCLUSIVEMENT une plateforme de mise en relation. AUCUNE transaction financière ne s'effectue via HorseConnex. Les paiements se font directement entre utilisateurs, en dehors de la Plateforme. HorseConnex n'est ni vendeur, ni acheteur, ni intermédiaire de paiement, ni dépositaire des fonds. Pour tout achat de cheval, un examen vétérinaire et un contrat écrit sont vivement recommandés.\n\n4bis. RISQUES, RENCONTRES EN PERSONNE ET ACTIVITÉS ÉQUESTRES (ACCEPTATION DES RISQUES)\nHorseConnex est uniquement un service de mise en relation et n'organise, ne supervise ni ne contrôle aucune interaction entre utilisateurs. Toute rencontre en personne, visite, essai, cours, transport, manipulation, achat, vente ou location de cheval, de matériel ou de service organisé via la Plateforme se fait aux RISQUES EXCLUSIFS des utilisateurs. L'ÉQUITATION ET LA MANIPULATION DES CHEVAUX COMPORTENT DES RISQUES INHÉRENTS DE BLESSURE GRAVE, VOIRE MORTELLE. HorseConnex ne vérifie ni l'identité, ni les compétences, ni les qualifications, ni les assurances, ni l'honnêteté des utilisateurs, ni l'état de santé, le tempérament, la valeur ou la conformité des chevaux, ni la sécurité des installations, véhicules ou prestations. L'utilisateur reconnaît et ACCEPTE D'ASSUMER L'INTÉGRALITÉ DE CES RISQUES et renonce, dans la mesure permise par la loi, à toute réclamation contre HorseConnex à ce titre. Recommandations de sécurité : rencontrer dans un lieu public ou sécurisé, vérifier l'identité et les références, exiger un contrat écrit et un examen vétérinaire pour tout achat, et souscrire les assurances appropriées (responsabilité civile, accident, cavalier, transport d'équidés).\n\n4ter. RANDONNÉES, ITINÉRAIRES ÉQUESTRES ET DONNÉES CARTOGRAPHIQUES (TRACÉS INDICATIFS)\nLes itinéraires, tracés, points d'intérêt et cartes proposés sur la Plateforme sont fournis à titre PUREMENT INDICATIF et informatif. Une partie de ces données provient de sources publiques tierces, notamment OpenStreetMap (© les contributeurs OpenStreetMap, licence ODbL), fournies « EN L'ÉTAT » sans garantie d'aucune sorte. HorseConnex NE GARANTIT PAS que les chemins sont exacts, ouverts, autorisés aux chevaux, sûrs, balisés, entretenus, praticables, ni conformes à la réglementation locale. Avant toute sortie, l'utilisateur doit VÉRIFIER PAR LUI-MÊME : les droits et autorisations de passage, l'état et la sécurité du terrain, la météo, la réglementation locale, sa propre aptitude et celle de son cheval, ainsi que les assurances appropriées. TOUTE RANDONNÉE, SORTIE OU UTILISATION D'UN ITINÉRAIRE SE FAIT AUX RISQUES EXCLUSIFS DE L'UTILISATEUR. L'équitation en extérieur comporte des risques inhérents de blessure grave, voire mortelle (chute, comportement du cheval, terrain, circulation, faune, tiers). Dans la mesure permise par la loi, HorseConnex décline toute responsabilité pour tout dommage, blessure, accident, perte, amende ou litige résultant de l'utilisation, de l'exactitude ou de l'indisponibilité d'un itinéraire, d'un tracé ou d'un point d'intérêt. Les itinéraires et points partagés par des utilisateurs relèvent de leur seule responsabilité ; l'utilisateur qui publie un tracé garantit disposer du droit de le faire et ne pas inciter à un passage illégal.\n\n4quater. CARNET DE SUIVI DU CHEVAL (OUTIL D'ORGANISATION — PAS UN CONSEIL VÉTÉRINAIRE)\nLe carnet de suivi est un outil personnel d'organisation et de mémorisation (vaccins, vermifuges, maréchalerie, soins, rappels). Il NE CONSTITUE PAS un conseil vétérinaire, médical ou professionnel et NE REMPLACE EN AUCUN CAS le suivi par un vétérinaire ou un professionnel qualifié. En cas de doute sur la santé d'un cheval, consultez un vétérinaire. L'utilisateur est seul responsable de l'exactitude, de la mise à jour et de la sauvegarde de ses informations. HorseConnex ne saurait être tenu responsable d'un rappel non reçu, en retard ou erroné, ni d'une donnée inexacte ou perdue. Le partage d'un carnet via un « lien de soin » relève de la seule responsabilité de l'utilisateur qui le génère et le diffuse ; il peut le révoquer à tout moment.\n\n4quinquies. ANNUAIRE DE PROFESSIONNELS ET DE GÎTES (FICHES PRO)\nLa Plateforme propose un annuaire de mise en relation avec des professionnels et des structures du monde équestre (dont hébergements et gîtes). Certaines fiches sont créées par les professionnels eux-mêmes ; d'autres peuvent être issues de sources publiques et ne pas être revendiquées. HorseConnex NE GARANTIT NI l'exactitude, ni l'actualité, ni la disponibilité, ni les qualifications, diplômes, assurances ou honnêteté des personnes ou structures référencées. Le badge « vérifié » correspond à un contrôle basique et NE constitue PAS une garantie de compétence, de sérieux ou de conformité. HorseConnex n'est PAS partie aux prestations, hébergements, contrats ou transactions convenus entre utilisateurs et professionnels, qui se déroulent aux risques exclusifs des parties. En réclamant une fiche, l'utilisateur atteste en être le titulaire légitime et répond de cette déclaration.\n\n5. RESPONSABILITÉ DES ANNONCES\n- L'utilisateur est seul responsable des informations publiées.\n- Les fiches écurie et disponibilités de boxes doivent être exactes et à jour.\n- Les cours pour enfants doivent respecter la législation locale en vigueur.\n- Photos : doivent appartenir à l'utilisateur ou être libres de droits.\n- HorseConnex peut supprimer tout contenu inexact, illicite ou frauduleux sans préavis.\n- Toute fausse annonce ou tentative d'arnaque entraîne suspension immédiate du compte.\n\n6. MODÉRATION ET INTELLIGENCE ARTIFICIELLE\nChaque annonce publiée est analysée automatiquement par un système d'intelligence artificielle (fourni par Google, voir la Politique de Confidentialité), afin de détecter les contenus inappropriés, injurieux, trompeurs ou frauduleux, le spam, les liens suspects et les tentatives de contournement de la messagerie. L'analyse fonctionne dans les langues de la Plateforme.\n- Une annonce jugée suspecte est SIGNALÉE à l'administrateur, qui l'examine et peut la retirer.\n- HorseConnex se réserve le droit de refuser ou de retirer une publication, avant ou après sa mise en ligne, sans préavis (voir article 5).\n- L'analyse automatisée est une AIDE à la modération : elle n'est ni exhaustive ni infaillible. L'absence de signalement NE VAUT NI approbation, NI garantie de conformité ou de véracité d'un contenu.\n- En cas d'indisponibilité du service d'analyse, un filtre de secours prend le relais : une panne technique n'empêche jamais un membre de publier.\n- Les utilisateurs peuvent signaler tout contenu via la fonction « Signaler ». Tout signalement est examiné.\n- DÉCISION HUMAINE (RGPD art. 22) : aucune décision produisant des effets juridiques à votre égard n'est prise sur le seul fondement d'un traitement automatisé. Le retrait d'un contenu ou la suspension d'un compte relève d'une décision humaine. Vous pouvez contester une décision de modération à legal@horseconnex.com.\n\n7. MESSAGERIE PRIVÉE\n- Les messages sont privés mais HorseConnex peut consulter leur contenu en cas de signalement.\n- Interdit : harcèlement, contenu sexuel, violence, escroquerie, spam, doxing.\n- Toute violation entraîne sanction du compte.\n- Conservation : messages stockés tant que la conversation existe ou jusqu'à suppression par l'utilisateur.\n\n8. GÉOLOCALISATION\n- L'utilisateur peut activer la géolocalisation pour voir les annonces autour de lui.\n- HorseConnex ne stocke PAS la position GPS exacte de l'utilisateur.\n- Pour les annonces, seules les coordonnées approximatives de la ville sont enregistrées.\n- Le bouton « Me localiser » et l'affichage des randonnées proches utilisent la géolocalisation de votre appareil, uniquement avec votre autorisation et de façon ponctuelle, pour centrer la carte. Votre position précise n'est PAS enregistrée sur nos serveurs.\n\n9. PROTECTION DES MINEURS (COPPA et équivalents)\n- Aucun utilisateur de moins de 13 ans n'est autorisé à créer un compte. Les mineurs de 13 à 17 ans doivent avoir le consentement d'un parent ou tuteur.\n- Les cours pour enfants doivent clairement indiquer les tranches d'âge.\n- Les annonceurs sont responsables du respect de la législation sur la protection des mineurs.\n- HorseConnex n'est pas responsable de la sécurité des mineurs lors d'activités tierces.\n- Aux États-Unis : conformité COPPA (Children's Online Privacy Protection Act).\n\n10. SYSTÈME D'AVIS\n- Les avis sont possibles UNIQUEMENT après contact via la messagerie interne.\n- Les avis doivent être honnêtes et basés sur une expérience réelle.\n- Faux avis ou avis diffamatoires : suppression et sanction du compte.\n- Vous pouvez modifier ou supprimer votre propre avis à tout moment.\n\n11. PROPRIÉTÉ INTELLECTUELLE\nMarque HorseConnex©, logo et contenus protégés. Reproduction interdite sans autorisation écrite. Les utilisateurs conservent les droits sur leurs photos mais accordent à HorseConnex une licence non-exclusive d'affichage sur la Plateforme.\n\n12. LIMITATION DE RESPONSABILITÉ\nHorseConnex fournit le service \"tel quel\" sans garantie. Nous ne sommes pas responsables :\n- Des dommages indirects, perte de profits, perte de données.\n- Des transactions entre utilisateurs.\n- Du contenu publié par les utilisateurs.\n- Des itinéraires, tracés, cartes et données cartographiques tierces (voir art. 4ter).\n- Des informations du carnet de suivi et des rappels (voir art. 4quater).\n- Des professionnels et gîtes référencés dans l'annuaire (voir art. 4quinquies).\n- Des pannes techniques, indisponibilité ou perte de données.\nResponsabilité maximale : 100 CHF par utilisateur et par an.\n\n13. DROIT À L'OUBLI\nVous pouvez supprimer votre compte à tout moment. La suppression entraîne :\n- Suppression de votre profil, annonces, messages, avis.\n- Conservation pendant 90 jours pour obligations légales.\n- Effacement définitif après ce délai.\n\n14. RÉSILIATION\nHorseConnex peut suspendre tout compte en cas de violation des CGU, sans préavis.\n\n15. MODIFICATIONS\nHorseConnex peut modifier ces CGU à tout moment. Les utilisateurs seront notifiés des changements importants par email ou notification dans l'app.\n\n16. LOI APPLICABLE\nDroit suisse pour les utilisateurs hors UE.\nDroit de l'UE (RGPD) pour les utilisateurs européens.\nDroit applicable selon la juridiction de l'utilisateur.\nJuridiction par défaut : tribunaux suisses (canton de Vaud).\n\n17. COOKIES ET TRACEURS\nLa Plateforme utilise différents types de cookies :\n- COOKIES ESSENTIELS : indispensables au fonctionnement (session, sécurité, préférences linguistiques). Toujours actifs, ne nécessitent pas de consentement.\n- COOKIES ANALYTIQUES : statistiques anonymes pour améliorer le service. Désactivables.\n- COOKIES MARKETING : personnalisation du contenu (annonces pertinentes). Désactivables.\nLors de votre première visite, un bandeau de consentement vous permet de choisir. Vous pouvez modifier vos préférences à tout moment dans les paramètres. Vous pouvez utiliser la Plateforme avec uniquement les cookies essentiels.\n\n18. PROTECTION ANTI-BOT (HCAPTCHA)\nPour protéger la Plateforme contre les bots et les attaques automatisées, nous utilisons hCaptcha (Intuition Machines, Inc.) sur les pages d'inscription, connexion et récupération de mot de passe. hCaptcha peut collecter votre adresse IP et des données de comportement. Voir leur politique : https://www.hcaptcha.com/privacy. Aucune donnée n'est partagée à des fins commerciales.\n\n19. DROITS RGPD / CCPA / LPD APPROFONDIS\nConformément aux lois en vigueur, vous disposez des droits suivants :\n- ARTICLE 15 RGPD : Droit d'accès à vos données.\n- ARTICLE 16 RGPD : Droit de rectification.\n- ARTICLE 17 RGPD : Droit à l'effacement (\"droit à l'oubli\") - via le bouton \"Supprimer mon compte\" dans Profil > Sécurité.\n- ARTICLE 20 RGPD : Droit à la portabilité - export de toutes vos données au format JSON via le bouton \"Télécharger mes données\" dans Profil > Sécurité.\n- ARTICLE 21 RGPD : Droit d'opposition.\n- CCPA \"DO NOT SELL\" : refuser la vente de vos données via le toggle dans Profil > Sécurité.\n- CCPA \"RIGHT TO KNOW\" : connaître les données collectées (voir Politique de Confidentialité).\n- LPD/nLPD (Suisse) : droits équivalents au RGPD.\nProcédure : exercice direct depuis le profil ou par email à legal@horseconnex.com (réponse sous 30 jours max).\n\n20. APPLICATION PROGRESSIVE (PWA)\nHorseConnex est une Progressive Web App (PWA) installable sur smartphone (iOS, Android), tablette et ordinateur. Une fois installée, l'app fonctionne comme une application native (icône sur l'écran d'accueil, mode plein écran). Aucune différence légale entre la version web et la version installée. La désinstallation n'efface pas vos données du serveur (utilisez le bouton \"Supprimer mon compte\" pour cela).\n\n21. LIMITES TECHNIQUES DE LA PLATEFORME\n- 5 photos maximum par annonce.\n- 20 Mo maximum par photo (annonces).\n- 5 Mo maximum pour la photo de profil.\n- 60 jours de durée d'annonce active (renouvelable).\n- 1 avis par utilisateur sur un autre utilisateur (modifiable/supprimable).\n- Restrictions selon plan (Gratuit / Pro / Pro Max - à venir).\nCes limites peuvent évoluer. Les utilisateurs seront informés des changements importants.\n\n22. DISPOSITIONS SPÉCIFIQUES USA / CALIFORNIE\n\n22.1 - DISCLAIMER \"AS IS\" / \"AS AVAILABLE\"\nLa Plateforme est fournie \"EN L\'ÉTAT\" (AS IS) et \"SELON DISPONIBILITÉ\" (AS AVAILABLE), sans garantie d\'aucune sorte, expresse ou implicite. HorseConnex EXCLUT TOUTES GARANTIES de qualité marchande (merchantability), d\'adéquation à un usage particulier (fitness for a particular purpose) et de non-contrefaçon (non-infringement), dans la mesure permise par la loi applicable.\n\n22.2 - LIMITATION OF LIABILITY (PLAFOND DE RESPONSABILITÉ)\nDans toute la mesure permise par la loi, la responsabilité totale cumulée de HorseConnex envers vous, pour tout dommage de quelque nature que ce soit, est LIMITÉE au plus élevé des deux montants suivants : (a) 100 USD ; ou (b) le montant total que vous avez payé sur la Plateforme au cours des 12 derniers mois précédant l\'événement à l\'origine du litige. HorseConnex NE SERA EN AUCUN CAS responsable des dommages indirects, consécutifs, accessoires, spéciaux, exemplaires ou punitifs (perte de profits, perte de données, perte de clientèle, etc.), même si HorseConnex a été informé de la possibilité de tels dommages.\n\n22.3 - INDEMNIFICATION (INDEMNISATION PAR L\'UTILISATEUR)\nVous acceptez de défendre, indemniser et tenir HorseConnex (ainsi que ses dirigeants, employés, agents et affiliés) inoffensif (HOLD HARMLESS) contre toute réclamation, demande, action, perte, dommage, coût ou dépense (y compris les frais et honoraires raisonnables d\'avocat), découlant de ou liés à : (a) votre utilisation de la Plateforme ; (b) vos contenus, annonces, messages ou avis publiés ; (c) votre violation des présentes CGU ; (d) votre violation de tout droit d\'un tiers (incluant droits de propriété intellectuelle, droits à la vie privée) ; (e) votre violation de toute loi applicable.\n\n22.4 - ARBITRAGE INDIVIDUEL OBLIGATOIRE (USA UNIQUEMENT)\nPOUR LES UTILISATEURS RÉSIDANT AUX ÉTATS-UNIS, vous acceptez que TOUT LITIGE, RÉCLAMATION OU CONTROVERSE découlant de ou lié aux présentes CGU ou à votre utilisation de la Plateforme sera RÉSOLU EXCLUSIVEMENT PAR ARBITRAGE INDIVIDUEL CONFIDENTIEL ET CONTRAIGNANT, et NON par un tribunal. L\'arbitrage sera administré par l\'American Arbitration Association (AAA) selon ses règles applicables, en langue anglaise, à San Francisco, California, USA. EXCEPTION : Vous ou HorseConnex pouvez intenter une action devant un small claims court (tribunal des petites créances) pour les litiges éligibles. EXCEPTION DE NON-PARTICIPATION (OPT-OUT) : Vous pouvez refuser cette clause d\'arbitrage en envoyant un email à legal@horseconnex.com dans les 30 jours suivant votre acceptation des présentes CGU, avec votre nom, adresse et déclaration explicite de refus.\n\n22.5 - RENONCIATION AUX ACTIONS COLLECTIVES (CLASS ACTION WAIVER)\nVOUS ET HORSECONNEX RENONCEZ EXPRESSÉMENT À TOUT DROIT DE PARTICIPER À UNE ACTION COLLECTIVE (CLASS ACTION), À UN ARBITRAGE COLLECTIF, À UNE ACTION DE GROUPE OU À TOUTE PROCÉDURE COLLECTIVE OU REPRÉSENTATIVE de quelque nature que ce soit contre l\'autre partie. Tous les litiges devront être résolus à titre INDIVIDUEL. Si cette clause est jugée inapplicable, l\'ensemble de la clause d\'arbitrage (22.4) sera nulle et non avenue.\n\n22.6 - DROIT APPLICABLE ET JURIDICTION USA\nPour les utilisateurs résidant aux États-Unis, les présentes CGU sont régies et interprétées conformément aux lois de l\'État de CALIFORNIE, USA, sans égard à ses dispositions sur les conflits de lois. Pour tout litige non soumis à arbitrage, la juridiction exclusive est le County of San Francisco, California, USA. Vous CONSENTEZ À LA JURIDICTION PERSONNELLE de ces tribunaux.\n\n22.7 - PROTECTION SECTION 230 (CDA - USA)\nHorseConnex est un \"INTERACTIVE COMPUTER SERVICE\" au sens de la Section 230 du Communications Decency Act of 1996 (47 U.S.C. § 230). À ce titre, HorseConnex N\'EST PAS considéré comme l\'éditeur ou le diffuseur des contenus publiés par ses utilisateurs (\"user-generated content\"). HorseConnex bénéficie de l\'immunité prévue par cette Section pour les contenus publiés par les utilisateurs et toute action de modération entreprise de bonne foi.\n\n22.8 - DMCA - PROCÉDURE DE NOTIFICATION ET RETRAIT\nConformément au Digital Millennium Copyright Act (17 U.S.C. § 512), pour signaler une violation présumée de copyright sur la Plateforme, envoyez une notification écrite à notre DMCA Agent à legal@horseconnex.com contenant : (a) signature physique ou électronique de la personne autorisée ; (b) identification de l\'œuvre protégée prétendument violée ; (c) URL exact du contenu litigieux ; (d) vos coordonnées complètes (nom, adresse, téléphone, email) ; (e) déclaration de bonne foi que l\'utilisation contestée n\'est pas autorisée ; (f) déclaration sous peine de parjure que les informations sont exactes et que vous êtes autorisé à agir au nom du titulaire des droits. CONTRE-NOTIFICATION : Si votre contenu a été retiré par erreur, vous pouvez envoyer une contre-notification au même email.\n\n22.9 - PROCÉDURE DE NOTIFICATION 30 JOURS\nAvant de déposer une demande d\'arbitrage, la partie plaignante doit envoyer une notification écrite détaillée à legal@horseconnex.com et permettre 30 jours pour résolution amiable.\n\n22.10 - DIVISIBILITÉ ET ACCORD COMPLET\nSi une disposition des présentes CGU est jugée invalide, illégale ou inapplicable, les autres dispositions resteront pleinement en vigueur (severability). Les présentes CGU constituent l\'accord complet (entire agreement) entre vous et HorseConnex concernant l\'utilisation de la Plateforme et remplacent tout accord antérieur.\n\n22.11 - FORCE MAJEURE\nHorseConnex ne sera pas responsable de tout retard ou défaut d\'exécution dû à des circonstances échappant à son contrôle raisonnable (catastrophes naturelles, guerres, pannes Internet, attaques cyber, décisions gouvernementales, pandémies, etc.).\n\nContact : legal@horseconnex.com",

    "Politique de confidentialité": "POLITIQUE DE CONFIDENTIALITÉ — HorseConnex\nVersion 2.4 — Septembre 2026\n\nResponsable du traitement : Mickaël Carron, personne physique, Suisse\nDélégué à la protection des données (DPO) : info@horseconnex.com\n\n1. DONNÉES COLLECTÉES\n- Identification : prénom, nom, email, téléphone, adresse, date de naissance.\n- Profil : photo, bio, activités équestres, catégories.\n- Annonces : titre, description, photos, prix, localisation (ville/région).\n- Messages : contenu de la messagerie privée.\n- Avis : notes et commentaires donnés/reçus.\n- Utilisation : annonces consultées, favoris, interactions.\n- Techniques : adresse IP, type d'appareil, navigateur, logs.\n- Géolocalisation : uniquement avec votre consentement explicite, pour afficher les annonces et randonnées proches et centrer la carte ; votre position précise n'est pas enregistrée sur nos serveurs.\n- Carnet de suivi : les informations que vous saisissez sur vos chevaux (nom, race, soins, vaccins, dates de rappel, notes) ; ces données vous appartiennent et ne sont partagées que si vous générez volontairement un « lien de soin ».\n\n2. FINALITÉS DU TRAITEMENT\n- Création et gestion de votre compte.\n- Mise en relation entre utilisateurs équestres.\n- Affichage des annonces et fonctions de la Plateforme.\n- Notifications (messages, avis, alertes).\n- Modération automatisée et manuelle des contenus.\n- Amélioration du service via analyses statistiques anonymes.\n- Obligations légales (lutte contre la fraude, conservation des preuves).\n\n3. BASES LÉGALES (RGPD Article 6)\n- Exécution du contrat (CGU acceptées).\n- Consentement (newsletters, géolocalisation).\n- Intérêt légitime (sécurité, lutte contre fraude).\n- Obligation légale (conservation données comptables).\n\n4. VOS DROITS\nConformément au RGPD (UE), LPD/nLPD (Suisse), CCPA (Californie) et autres lois applicables, vous avez les droits :\n- ACCÈS : obtenir une copie de vos données.\n- RECTIFICATION : corriger des données inexactes (depuis votre profil).\n- EFFACEMENT (\"droit à l'oubli\") : supprimer votre compte (Profil > Sécurité > Supprimer mon compte).\n- LIMITATION : restreindre le traitement.\n- PORTABILITÉ : exporter toutes vos données au format JSON (Profil > Sécurité > Télécharger mes données).\n- OPPOSITION : vous opposer à certains traitements.\n- DO NOT SELL (CCPA - Californie) : refuser la vente de vos données (Profil > Sécurité > Do Not Sell My Data).\n- RÉCLAMATION : auprès de l'autorité de protection des données (PFPDT en Suisse, CNIL en France, DPC en Irlande, AG en Californie, etc.).\n\nLa plupart des droits sont exerçables directement depuis votre profil. Pour toute autre demande : info@horseconnex.com (réponse sous 30 jours max).\n\n5. DURÉE DE CONSERVATION\n- Compte actif : durée de l'utilisation.\n- Compte inactif : 2 ans après dernière connexion, puis suppression automatique.\n- Compte supprimé : 90 jours pour obligations légales, puis effacement définitif.\n- Logs techniques : 12 mois.\n\n6. PARTAGE DES DONNÉES\nVos données ne sont JAMAIS vendues. Partagées uniquement avec :\n- Vercel Inc. (hébergement) : USA, certifié RGPD via Standard Contractual Clauses.\n- Supabase (base de données) : Suisse (Zurich) — pays bénéficiant d'une décision d'adéquation de la Commission européenne, conforme RGPD et LPD/nLPD.\n- Nominatim / OpenStreetMap (géocodage, recherche de lieu et fond de carte) : lorsque vous recherchez un lieu (ex : une ville), le terme saisi est envoyé pour localiser la carte ; aucune donnée personnelle identifiante n'est transmise. Les données cartographiques et d'itinéraires proviennent d'OpenStreetMap (© les contributeurs OpenStreetMap, licence ODbL).\n- hCaptcha / Intuition Machines, Inc. (protection anti-bot) : adresse IP + données comportementales pour sécurité uniquement. Politique : https://www.hcaptcha.com/privacy.\n- Infomaniak (envoi emails transactionnels) : Suisse, conforme LPD/RGPD.\n- Google (Gemini API) : le texte de vos annonces (titre, description) et vos requêtes de recherche peuvent être transmis à l'IA de Google pour la rédaction assistée, la traduction automatique, la recherche intelligente et l'aide à la modération. Conforme à la politique de Google (https://policies.google.com/privacy). Seuls les contenus nécessaires sont envoyés, au moment où vous utilisez ces fonctions ; vos coordonnées (email, téléphone, adresse) ne sont pas transmises à l'IA.\n- Google (connexion avec un compte Google) : si vous choisissez « Se connecter avec Google », Google traite votre identification (adresse email, nom et photo de profil de votre compte Google) afin de vous authentifier. Nous ne lui transmettons aucune autre donnée. Cette option est facultative : vous pouvez créer un compte avec une adresse email et un mot de passe.\n- Google Firebase Cloud Messaging (notifications de l'application Android) : pour faire parvenir une notification à votre téléphone, l'identifiant technique de votre appareil ainsi que le titre et le libellé de la notification transitent par les serveurs de Google — c'est le seul chemin possible vers un appareil Android. Le contenu de vos messages privés n'y figure pas : la notification indique uniquement qu'un message vous attend, et il se lit dans l'application. Sur le site web, le contenu des notifications est chiffré par le protocole Web Push et reste illisible pour le service qui les distribue. Vous pouvez désactiver les notifications à tout moment (Profil > Sécurité).\n- Autorités légales : uniquement sur demande judiciaire.\n\n7. TRANSFERTS INTERNATIONAUX\nVos données peuvent transiter par les USA via Vercel. Protections en place :\n- Standard Contractual Clauses (SCC) UE-USA.\n- Chiffrement TLS 1.3 en transit.\n- Chiffrement AES-256 au repos.\n\n8. SÉCURITÉ\n- Chiffrement SSL/TLS pour toutes les communications.\n- Mots de passe hashés avec bcrypt.\n- Base de données hébergée en Suisse (Zurich).\n- Modération IA pour détecter les comportements suspects.\n- Authentification à 2 facteurs (2FA) disponible (à venir).\n\n9. COOKIES\nLa Plateforme utilise 3 catégories de cookies :\n- ESSENTIELS (toujours actifs) : session de connexion, sécurité, préférences linguistiques. Indispensables au fonctionnement.\n- ANALYTIQUES (avec votre consentement) : statistiques anonymes pour améliorer le service. Vous pouvez les désactiver.\n- MARKETING (avec votre consentement) : personnalisation du contenu. Vous pouvez les désactiver.\nLors de votre première visite, un bandeau de consentement vous permet de choisir. Vos préférences peuvent être modifiées à tout moment. AUCUN cookie publicitaire tiers (pas de Google Ads, Facebook Pixel, etc.).\n\n10. ENFANTS\nLa Plateforme n'est pas destinée aux moins de 13 ans. Les mineurs de 13 à 17 ans doivent avoir le consentement d'un parent ou tuteur. Si nous découvrons qu'un compte appartient à un enfant de moins de 13 ans, il est immédiatement supprimé. Conformité COPPA (USA).\n\n11. NOTIFICATION DE VIOLATION\nEn cas de fuite de données, vous serez notifié par email dans les 72h, conformément au RGPD Article 34.\n\n12. MODIFICATIONS\nNous pouvons modifier cette politique. Vous serez informé par email ou notification dans l'app.\n\n13. CONTACT\nResponsable / DPO : info@horseconnex.com\nQuestions légales : legal@horseconnex.com\nSupport : support@horseconnex.com",

    "Mentions légales": "MENTIONS LÉGALES — HorseConnex\nVersion 1.3 — Août 2026\n\nÉDITEUR\nMickaël Carron — éditeur indépendant (personne physique), Suisse\nSiège social : Suisse\nEmails :\n- info@horseconnex.com (général + DPO)\n- support@horseconnex.com (support utilisateurs)\n- legal@horseconnex.com (questions juridiques)\n\nDIRECTEUR DE LA PUBLICATION\nMickaël Carron, responsable de la publication\n\nHÉBERGEMENT\nVercel Inc.\n340 S Lemon Ave #4133, Walnut, CA 91789, USA\nhttps://vercel.com\n\nBASE DE DONNÉES\nSupabase (Cloud)\nDatacenter : Zurich, Suisse\nhttps://supabase.com\n\nEMAILS TRANSACTIONNELS\nInfomaniak Network SA\nRue Eugene Marziano 25, 1227 Genève, Suisse\nhttps://www.infomaniak.com\n\nPROTECTION ANTI-BOT\nIntuition Machines, Inc. (hCaptcha)\nUSA\nhttps://www.hcaptcha.com\n\nPROPRIÉTÉ INTELLECTUELLE\n© 2025-2026 Mickaël Carron / HorseConnex. Tous droits réservés.\nMarque HorseConnex©, logo, design, code source et contenus protégés par :\n- Droit d'auteur suisse (LDA)\n- Droit d'auteur de l'Union européenne\n- DMCA (USA)\nReproduction, modification, distribution interdites sans autorisation écrite.\n\nMARQUES\nLes autres marques mentionnées appartiennent à leurs propriétaires respectifs.\n\nRESPONSABILITÉ\nPlateforme de mise en relation uniquement.\nNon responsable des transactions, accords ou litiges entre utilisateurs.\nNon responsable du contenu publié par les utilisateurs.\n\nDROIT APPLICABLE\n- Suisse : LPD / nLPD\n- UE : RGPD\n- USA : CCPA (Californie), COPPA (mineurs)\n- Autres : législation locale applicable\n\nJURIDICTION COMPÉTENTE\nTribunaux suisses (canton de Vaud) pour les utilisateurs hors UE.\nTribunaux du pays de résidence pour les utilisateurs UE (RGPD).\n\nMÉDIATION\nEn cas de litige, possibilité de médiation via :\n- Office fédéral de la justice (Suisse)\n- Centre européen des consommateurs (UE)\n\nSIGNALEMENT DE CONTENU ILLICITE\nlegal@horseconnex.com\n\nDMCA (USA - signalement de violation de copyright)\nDMCA agent : legal@horseconnex.com\n\nContact légal : legal@horseconnex.com",
  };
  if(doc) return (
    <div style={{
      minHeight:"100vh",
      background:"radial-gradient(ellipse 80% 60% at 50% -10%, rgba(255,180,120,.08) 0%, transparent 50%), radial-gradient(ellipse at top, #3B1F6E 0%, #1F1A4A 28%, #14132E 58%, #08081A 100%)",
      color:"#fff", display:"flex", flexDirection:"column",
      position:"relative", overflow:"hidden",
    }}>
      <G/>
      {/* Header glassmorphism dark */}
      <div style={{
        position:"sticky", top:0, zIndex:10,
        padding:"16px 18px 14px",
        background:"rgba(13,13,26,.7)",
        backdropFilter:"blur(14px)",
        borderBottom:"1px solid rgba(255,255,255,.1)",
        display:"flex", alignItems:"center", gap:12,
      }}>
        <motion.button whileTap={{ scale: 0.92 }} onClick={()=>setDoc(null)} style={{background:"rgba(255,255,255,.08)",border:"1px solid rgba(255,255,255,.12)",borderRadius:10,width:36,height:36,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0,color:"#fff",fontSize:18}}>←</motion.button>
        <div style={{fontFamily:"var(--fh)",fontSize:16,fontWeight:900,color:"#fff",letterSpacing:.3,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{doc}</div>
      </div>
      <div style={{padding:"16px 18px 90px", flex:1, overflowY:"auto", position:"relative", zIndex:1}}>
        <div style={{
          background:"#fff", borderRadius:14, padding:18,
          boxShadow:"0 14px 32px rgba(0,0,0,.4), 0 0 0 1px rgba(255,255,255,.05)",
        }}>
          <p style={{fontSize:13, color:"#1A1A2E", lineHeight:1.8, margin:0, whiteSpace:"pre-wrap"}}>{docs[doc]}</p>
        </div>
      </div>
      <div style={{
        position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)",
        width:"100%", maxWidth:430,
        padding:"12px 18px 18px",
        background:"rgba(13,13,26,.85)", backdropFilter:"blur(14px)",
        borderTop:"1px solid rgba(255,255,255,.08)",
        zIndex:5,
      }}>
        <motion.button whileTap={{ scale: 0.97 }} onClick={()=>setDoc(null)} style={{
          width:"100%",
          background:"rgba(255,255,255,.06)",
          color:"#fff",
          border:"1px solid rgba(255,255,255,.18)",
          borderRadius:14, padding:"14px 24px",
          fontSize:14, fontWeight:800, fontFamily:"var(--fh)",
          letterSpacing:.5, cursor:"pointer",
          backdropFilter:"blur(10px)",
        }}>← Retour</motion.button>
      </div>
    </div>
  );

  return (
    <div style={{
      minHeight:"100vh",
      background:"radial-gradient(ellipse 80% 60% at 50% -10%, rgba(255,180,120,.08) 0%, transparent 50%), radial-gradient(ellipse at top, #3B1F6E 0%, #1F1A4A 28%, #14132E 58%, #08081A 100%)",
      color:"#fff", display:"flex", flexDirection:"column",
      position:"relative", overflow:"hidden",
    }}>
      <G/>
      <AuthSparkles />

      {/* Hero glassmorphism dark + logo sticker + halo */}
      <div style={{
        padding:"calc(env(safe-area-inset-top, 24px) + 14px) 20px 20px",
        textAlign:"center", position:"relative", zIndex:1,
      }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          style={{position:"relative", display:"inline-block"}}
        >
          <motion.div
            animate={{ opacity: [0.4, 0.7, 0.4], scale: [0.95, 1.05, 0.95] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            style={{position:"absolute", inset:-18, borderRadius:"50%", background:"radial-gradient(circle, rgba(200,0,26,.35) 0%, transparent 70%)", filter:"blur(14px)", zIndex:0}}
          />
          <img src="/horseconnex_logo_sticker_rouge.png" alt="" style={{width:64, height:64, objectFit:"contain", filter:"drop-shadow(0 8px 20px rgba(200,0,26,.5))", position:"relative", zIndex:1}}/>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={{fontFamily:"var(--fh)", fontSize:21, fontWeight:900, color:"#fff", marginTop:10, letterSpacing:.5}}
        >CONDITIONS D'UTILISATION</motion.div>
      </div>

      <div style={{flex:1, padding:"6px 18px 110px", overflowY:"auto", position:"relative", zIndex:1}}>
        {Object.keys(docs).map((doc,i)=>(
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 + i*0.05 }}
            whileTap={{ scale: 0.98 }}
            onClick={()=>setDoc(doc)}
            style={{
              background:"rgba(255,255,255,.05)",
              backdropFilter:"blur(12px)",
              border:"1px solid rgba(255,255,255,.1)",
              borderRadius:12, padding:"14px 16px", marginBottom:8,
              display:"flex", justifyContent:"space-between", alignItems:"center",
              cursor:"pointer",
            }}
          >
            <div>
              <div style={{fontSize:13.5, fontWeight:700, color:"#fff", fontFamily:"var(--fh)"}}>{doc}</div>
              <div style={{fontSize:11, color:"rgba(255,255,255,.55)", marginTop:2}}>Appuyer pour lire →</div>
            </div>
            <CIcon name="chevron" s={16} c="rgba(255,255,255,.45)"/>
          </motion.div>
        ))}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          style={{marginTop:14, padding:14, background:"rgba(34,197,94,.08)", border:"1px solid rgba(34,197,94,.25)", borderRadius:12}}
        >
          <p style={{fontSize:12.5, color:"rgba(255,255,255,.75)", lineHeight:1.7, margin:0}}>
            🔒 En créant un compte, vous acceptez nos conditions. Vos données sont protégées conformément au RGPD.
          </p>
        </motion.div>
        <div style={{marginTop:18, padding:"14px 14px", background:"rgba(255,255,255,.05)", border:"1px solid rgba(255,255,255,.1)", borderRadius:12, display:"flex", flexDirection:"column", gap:14}}>
          <Chk on={ok} toggle={()=>setOk(!ok)} label="J'ai lu et j'accepte les conditions générales et la politique de confidentialité" dark/>
          <Chk on={cookiesOk} toggle={()=>setCookiesOk(!cookiesOk)} label="J'accepte les cookies de mesure d'audience (optionnel — les cookies essentiels restent toujours actifs)" dark/>
        </div>
      </div>

      <div style={{
        position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)",
        width:"100%", maxWidth:430,
        padding:"12px 18px 18px",
        background:"rgba(13,13,26,.85)", backdropFilter:"blur(14px)",
        borderTop:"1px solid rgba(255,255,255,.08)",
        zIndex:5,
      }}>
        <PrimaryButton onClick={()=>{try{localStorage.setItem("hc_cookies_consent", cookiesOk ? "all" : "essential");}catch(e){} onAccept();}} disabled={!ok}>Continuer →</PrimaryButton>
      </div>
    </div>
  );
}

// === AuthChoice ===
// === Étincelles de fond pour les écrans dark (réutilise le pattern de la landing v1) ===
// 28 étoiles qui pop + disparaissent. Mémorisées (positions stables au re-render).
function AuthSparkles() {
  const stars = useMemo(() =>
    Array.from({ length: 28 }, (_, i) => {
      const palette = Math.random();
      const color = palette < 0.6
        ? { fill: "#fff", glow: "rgba(255,255,255,.95)" }
        : palette < 0.85
          ? { fill: "#FF5A6E", glow: "rgba(255,90,110,.95)" }
          : { fill: "#FFD278", glow: "rgba(255,210,120,.95)" };
      return {
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: 2.5 + Math.random() * 5,
        duration: 1.0 + Math.random() * 1.2,
        repeatDelay: 0.4 + Math.random() * 2.0,
        initialDelay: Math.random() * 1.8,
        fill: color.fill,
        glow: color.glow,
      };
    }), []);
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
      {stars.map(s => (
        <motion.div
          key={s.id}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: [0, 1, 0], scale: [0, 1.3, 0] }}
          transition={{
            duration: s.duration,
            delay: s.initialDelay,
            repeat: Infinity,
            repeatDelay: s.repeatDelay,
            ease: "easeOut",
            times: [0, 0.45, 1],
          }}
          style={{
            position: "absolute",
            top: `${s.y}%`,
            left: `${s.x}%`,
            width: s.size,
            height: s.size,
            borderRadius: "50%",
            background: s.fill,
            boxShadow: `0 0 ${s.size * 3}px ${s.glow}, 0 0 ${s.size * 6}px ${s.glow}`,
          }}
        />
      ))}
    </div>
  );
}

export function AuthChoice({onFirst,onLogin,onGuest,lang="fr"}) {
  const t = useLang(lang);
  // Connexion / inscription via Google (OAuth Supabase). En cliquant, l'utilisateur
  // accepte les CGU + Confidentialité (mention affichée juste sous les boutons).
  // Nécessite que le provider Google soit activé côté Supabase (Auth → Providers).
  const signInGoogle = async () => {
    try {
      await signInWithGoogle();
    } catch (e) {
      alert(t.googleUnavailable || "Connexion Google indisponible pour le moment. Réessaie plus tard.");
    }
  };
  return (
    <div style={{
      minHeight: "100vh",
      background: "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(255,180,120,.08) 0%, transparent 50%), radial-gradient(ellipse at top, #3B1F6E 0%, #1F1A4A 28%, #14132E 58%, #08081A 100%)",
      color: "#fff",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      overflow: "hidden",
      padding: "32px 24px",
    }}>
      <G/>

      {/* Halos de lumière en fond */}
      <motion.div
        animate={{ opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 6, repeat: Infinity }}
        style={{
          position: "absolute",
          top: "-15%", left: "-10%",
          width: 360, height: 360,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(200,0,26,.4) 0%, transparent 70%)",
          filter: "blur(40px)",
          pointerEvents: "none",
        }}
      />
      <motion.div
        animate={{ opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 8, repeat: Infinity, delay: 2 }}
        style={{
          position: "absolute",
          bottom: "-15%", right: "-10%",
          width: 320, height: 320,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(124,58,237,.3) 0%, transparent 70%)",
          filter: "blur(40px)",
          pointerEvents: "none",
        }}
      />

      {/* Étincelles cinéma en fond */}
      <AuthSparkles />

      {/* Logo central avec halo pulsant */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        style={{ position: "relative", marginBottom: 22, zIndex: 1 }}
      >
        <motion.div
          animate={{ opacity: [0.4, 0.7, 0.4], scale: [0.95, 1.05, 0.95] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            inset: -22,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(200,0,26,.35) 0%, transparent 70%)",
            filter: "blur(16px)",
            zIndex: 0,
          }}
        />
        <img
          src="/horseconnex_logo_sticker_rouge.png"
          alt=""
          style={{
            width: 140, height: 140, objectFit: "contain",
            filter: "drop-shadow(0 12px 36px rgba(200,0,26,.5))",
            position: "relative", zIndex: 1,
          }}
        />
      </motion.div>

      {/* Texte d'accueil */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.25 }}
        style={{ textAlign: "center", marginBottom: 28, zIndex: 1 }}
      >
        <div style={{
          fontFamily: "var(--fh)",
          fontSize: 26, fontWeight: 900,
          color: "#fff",
          letterSpacing: -0.5,
          marginBottom: 8,
        }}>
          {t.welcome}
        </div>
        <div style={{
          fontSize: 13,
          color: "rgba(255,255,255,.65)",
          lineHeight: 1.5,
          maxWidth: 320,
        }}>
          {t.welcomeSub}
        </div>
      </motion.div>

      {/* 3 boutons d'action */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        style={{
          width: "100%", maxWidth: 340,
          display: "flex", flexDirection: "column", gap: 10,
          zIndex: 1,
        }}
      >
        {/* CTA principal Créer compte */}
        <PrimaryButton onClick={onFirst}>🚀  {t.createAccount}</PrimaryButton>

        {/* Continuer avec Google (OAuth) */}
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={signInGoogle}
          style={{
            width: "100%",
            padding: "13px 24px",
            background: "#fff",
            color: "#1f1f1f",
            border: "none",
            borderRadius: 14,
            fontSize: 14, fontWeight: 700,
            fontFamily: "var(--fh)",
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            boxShadow: "0 4px 14px rgba(0,0,0,.25)",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          {t.continueGoogle || "Continuer avec Google"}
        </motion.button>

        {/* Login (secondaire) */}
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={onLogin}
          style={{
            width: "100%",
            padding: "15px 24px",
            background: "rgba(255,255,255,.06)",
            color: "#fff",
            border: "1px solid rgba(255,255,255,.18)",
            borderRadius: 14,
            fontSize: 14, fontWeight: 700,
            fontFamily: "var(--fh)",
            cursor: "pointer",
            backdropFilter: "blur(10px)",
          }}
        >
          {t.alreadyAccount}
        </motion.button>

        {/* Mode découverte (guest) */}
        {onGuest && (
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={onGuest}
            style={{
              width: "100%",
              padding: "13px 24px",
              background: "rgba(34,197,94,.06)",
              color: "#22c55e",
              border: "1.5px dashed rgba(34,197,94,.45)",
              borderRadius: 14,
              fontSize: 13, fontWeight: 800,
              fontFamily: "var(--fh)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <span style={{ fontSize: 16 }}>👀</span>
            <span>{t.discoverAds}</span>
            <span>→</span>
          </motion.button>
        )}
        {onGuest && (
          <div style={{ textAlign: "center", fontSize: 11, color: "rgba(255,255,255,.4)" }}>
            {t.discoverSub}
          </div>
        )}

        {/* Mention légale : couvre aussi les visiteurs invités (RGPD/CCPA/LPD) */}
        <div style={{ textAlign: "center", fontSize: 10.5, color: "rgba(255,255,255,.45)", lineHeight: 1.6, marginTop: 6 }}>
          {t.authLegalNotice || "En continuant, vous acceptez nos"}{" "}
          <a href={legalUrl("terms", lang)} target="_blank" rel="noopener noreferrer" style={{ color: "rgba(255,255,255,.72)", textDecoration: "underline", textUnderlineOffset: 2 }}>{t.readTerms || "Conditions générales"}</a>
          {" · "}
          <a href={legalUrl("privacy", lang)} target="_blank" rel="noopener noreferrer" style={{ color: "rgba(255,255,255,.72)", textDecoration: "underline", textUnderlineOffset: 2 }}>{t.readPrivacy || "Confidentialité"}</a>
        </div>
      </motion.div>

      {/* Badge identité en bas */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.7 }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "7px 14px",
          marginTop: 28,
          background: "rgba(255,255,255,.05)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255,255,255,.12)",
          borderRadius: 100,
          fontSize: 10.5, fontWeight: 600,
          color: "rgba(255,255,255,.78)",
          letterSpacing: 0.6,
          zIndex: 1,
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ fontSize: 12 }}>🇨🇭</span>
          Swiss made
        </span>
        <span style={{ width: 3, height: 3, borderRadius: "50%", background: "rgba(255,255,255,.4)" }} />
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ fontSize: 11 }}>🔒</span>
          Privacy-first
        </span>
      </motion.div>
    </div>
  );
}


// === ForgotPasswordScreen ===
export function ForgotPasswordScreen({onBack, showToast, lang="fr"}) {
  const t = useLang(lang);
  const [captchaTokenFp, setCaptchaTokenFp] = useState(null);
  const [captchaFailed, setCaptchaFailed] = useState(false);
  const captchaRefFp = useRef(null);
  const captchaWidgetFp = useRef(null);
  
  useEffect(() => {
    let tries = 0;
    const interval = setInterval(() => {
      tries++;
      if (window.hcaptcha && captchaRefFp.current && captchaWidgetFp.current === null) {
        try {
          captchaWidgetFp.current = window.hcaptcha.render(captchaRefFp.current, {
            sitekey: "82d6f3d6-3b54-4220-854e-7f663662e8fa",
            size: "compact",
            callback: (token) => setCaptchaTokenFp(token),
            "expired-callback": () => setCaptchaTokenFp(null),
            "error-callback": () => setCaptchaTokenFp(null)
          });
          clearInterval(interval);
        } catch(e) { setCaptchaFailed(true); clearInterval(interval); }
      } else if (tries >= 30) { setCaptchaFailed(true); clearInterval(interval); }
    }, 200);
    return () => clearInterval(interval);
  }, []);
  
  const [email,setEmail]=useState("");
  const [sent,setSent]=useState(false);
  const [loading,setLoading]=useState(false);

  const handle = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if(!email||!emailRegex.test(email)){showToast({type:"error",text:t.errEmailInvalid});return;}
    setLoading(true);
    try {
      if (!captchaTokenFp) {
        showToast({type:"error",text:captchaFailed?t.errCaptchaBlocked:t.errCaptcha});
        setLoading(false);
        return;
      }
      // Envoi via le client implicit (pas de code_verifier stocké) → le lien de
      // reset revient avec les jetons dans le #fragment → marche cross-device
      // (webmail, appli mail sur un autre navigateur). Cf. supabaseResetSender.
      const { error } = await supabaseResetSender.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + "/reset-password",
        captchaToken: captchaTokenFp
      });
      if (error) {
        showToast({type:"error",text:t.errPrefix+error.message});
        try { if (window.hcaptcha && captchaWidgetFp.current !== null) window.hcaptcha.reset(captchaWidgetFp.current); } catch (e) {}
        setCaptchaTokenFp(null);
        setLoading(false);
        return;
      }
      setSent(true);
    } catch(err) {
      showToast({type:"error",text:t.errConnection});
      console.error(err);
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(255,180,120,.08) 0%, transparent 50%), radial-gradient(ellipse at top, #3B1F6E 0%, #1F1A4A 28%, #14132E 58%, #08081A 100%)",
      color: "#fff",
      display: "flex", flexDirection: "column",
      position: "relative", overflow: "hidden",
    }}>
      <G/>
      <AuthSparkles />

      {/* Header glassmorphism dark */}
      <div style={{
        position: "sticky", top: 0, zIndex: 10,
        padding: "16px 18px 14px",
        background: "rgba(13,13,26,.55)",
        backdropFilter: "blur(14px)",
        borderBottom: "1px solid rgba(255,255,255,.1)",
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <motion.button
          whileTap={{ scale: 0.92 }}
          onClick={onBack}
          style={{
            background: "rgba(255,255,255,.08)",
            border: "1px solid rgba(255,255,255,.12)",
            borderRadius: 10, width: 36, height: 36,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", flexShrink: 0, color: "#fff", fontSize: 18,
          }}
        >←</motion.button>
        <div style={{fontFamily: "var(--fh)", fontSize: 18, fontWeight: 900, color: "#fff", letterSpacing: 0.3, flex: 1}}>
          {t.forgotPassword}
        </div>
        <img src="/horseconnex_logo_sticker_rouge.png" alt="" style={{width: 32, height: 32, objectFit: "contain", filter: "drop-shadow(0 4px 12px rgba(200,0,26,.5))"}}/>
      </div>

      <div style={{flex:1, padding:"24px 18px 110px", display:"flex", flexDirection:"column", gap:14, position:"relative", zIndex:1}}>
        {sent ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1.4, 0.36, 1] }}
            style={{textAlign:"center", padding:"40px 20px"}}
          >
            <div style={{
              width:80, height:80, borderRadius:"50%",
              background:"rgba(34,197,94,.15)",
              border:"1px solid rgba(34,197,94,.35)",
              display:"flex", alignItems:"center", justifyContent:"center",
              margin:"0 auto 20px",
              boxShadow:"0 0 30px rgba(34,197,94,.3)",
            }}>
              <CIcon name="mail" s={36} c="#22c55e"/>
            </div>
            <div style={{fontFamily:"var(--fh)", fontSize:24, fontWeight:900, color:"#fff", marginBottom:10, letterSpacing:-0.2}}>{t.emailSent}</div>
            <div style={{fontSize:13, color:"rgba(255,255,255,.65)", lineHeight:1.7, marginBottom:8}}>
              {t.resetLinkIntro}
            </div>
            <div style={{fontSize:14, fontWeight:700, color:"#FF5A6E", marginBottom:14, wordBreak:"break-all"}}>{email}</div>
            <div style={{fontSize:13, color:"rgba(255,255,255,.82)", lineHeight:1.6, marginBottom:24, background:"rgba(255,255,255,.06)", border:"1px solid rgba(255,255,255,.12)", borderRadius:12, padding:"10px 12px"}}>
              {t.checkSpam}
            </div>
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={onBack}
              style={{
                width:"100%",
                background:"var(--app-cta-bg)",
                color:"#fff", border:"none", borderRadius:14,
                padding:"14px 24px",
                fontSize:15, fontWeight:800, fontFamily:"var(--fh)",
                letterSpacing:.5, cursor:"pointer",
                boxShadow:"0 8px 22px rgba(200,0,26,.45), inset 0 1px 0 rgba(255,255,255,.18)",
              }}
            >{t.backToLogin}</motion.button>
          </motion.div>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{
                background:"rgba(255,255,255,.05)",
                backdropFilter:"blur(12px)",
                border:"1px solid rgba(255,255,255,.1)",
                borderRadius:14, padding:16,
              }}
            >
              <p style={{fontSize:13, color:"rgba(255,255,255,.75)", lineHeight:1.7, margin:0}}>
                {t.forgotPwdIntro}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.08 }}
              style={{
                background:"#fff",
                borderRadius:16, padding:18,
                boxShadow:"0 12px 30px rgba(0,0,0,.4), 0 0 0 1px rgba(255,255,255,.05)",
              }}
            >
              <Field label={t.email} placeholder={t.emailPh||"votre@email.com"} type="email" icon="mail" value={email} onChange={e=>setEmail(e.target.value)} required autoComplete="email" inputMode="email" name="email"/>
            </motion.div>
          </>
        )}
      </div>
      {!sent && (
        <div style={{
          position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)",
          width:"100%", maxWidth:430,
          background:"rgba(13,13,26,.85)", backdropFilter:"blur(14px)",
          borderTop:"1px solid rgba(255,255,255,.08)",
          padding:"12px 20px 18px",
          zIndex:5,
        }}>
          <div ref={captchaRefFp} style={{display:"flex", justifyContent:"center", margin:"4px 0 12px"}}/>
          {captchaFailed && <div style={{color:"#FF5A6E", fontSize:12.5, lineHeight:1.4, textAlign:"center", padding:"0 14px", marginBottom:8}}>{t.errCaptchaBlocked}</div>}
          <PrimaryButton onClick={handle} loading={loading}>{loading?t.sending:t.resetPassword}</PrimaryButton>
        </div>
      )}
    </div>
  );
}


// === ResetPasswordScreen ===
// Affiche le formulaire "nouveau mot de passe" quand l'utilisateur arrive via le
// lien de l'email de reinitialisation (URL /reset-password). A ce stade, le lien
// a deja cree une session de recuperation cote Supabase -> updateUser({password})
// definit le nouveau mot de passe. i18n local (6 langues) pour rester autonome.
const RP_I18N = {
  fr: { title:"Nouveau mot de passe", intro:"Choisis un nouveau mot de passe pour ton compte.", newL:"Nouveau mot de passe", confL:"Confirme le mot de passe", submit:"Enregistrer le mot de passe", saving:"Enregistrement…", success:"Mot de passe mis à jour ! Tu es connecté.", mismatch:"Les deux mots de passe ne correspondent pas.", weak:"Mot de passe trop faible : il manque ", err:"Impossible de mettre à jour le mot de passe. Réessaie.", expired:"Lien expiré ou invalide. Redemande un email de réinitialisation depuis « Mot de passe oublié ».", reqLength:"8 caractères", reqUpper:"une majuscule", reqLower:"une minuscule", reqDigit:"un chiffre", reqSpecial:"un caractère spécial" },
  en: { title:"New password", intro:"Choose a new password for your account.", newL:"New password", confL:"Confirm password", submit:"Save new password", saving:"Saving…", success:"Password updated! You are logged in.", mismatch:"The two passwords do not match.", weak:"Password too weak: missing ", err:"Could not update the password. Please try again.", expired:"Link expired or invalid. Request a new reset email from “Forgot password”.", reqLength:"8 characters", reqUpper:"an uppercase letter", reqLower:"a lowercase letter", reqDigit:"a digit", reqSpecial:"a special character" },
  de: { title:"Neues Passwort", intro:"Wähle ein neues Passwort für dein Konto.", newL:"Neues Passwort", confL:"Passwort bestätigen", submit:"Neues Passwort speichern", saving:"Speichern…", success:"Passwort aktualisiert! Du bist angemeldet.", mismatch:"Die beiden Passwörter stimmen nicht überein.", weak:"Passwort zu schwach: es fehlt ", err:"Passwort konnte nicht aktualisiert werden. Bitte erneut versuchen.", expired:"Link abgelaufen oder ungültig. Fordere über „Passwort vergessen“ eine neue E-Mail an.", reqLength:"8 Zeichen", reqUpper:"ein Großbuchstabe", reqLower:"ein Kleinbuchstabe", reqDigit:"eine Ziffer", reqSpecial:"ein Sonderzeichen" },
  it: { title:"Nuova password", intro:"Scegli una nuova password per il tuo account.", newL:"Nuova password", confL:"Conferma password", submit:"Salva la password", saving:"Salvataggio…", success:"Password aggiornata! Hai effettuato l'accesso.", mismatch:"Le due password non coincidono.", weak:"Password troppo debole: manca ", err:"Impossibile aggiornare la password. Riprova.", expired:"Link scaduto o non valido. Richiedi una nuova email da « Password dimenticata ».", reqLength:"8 caratteri", reqUpper:"una maiuscola", reqLower:"una minuscola", reqDigit:"una cifra", reqSpecial:"un carattere speciale" },
  es: { title:"Nueva contraseña", intro:"Elige una nueva contraseña para tu cuenta.", newL:"Nueva contraseña", confL:"Confirma la contraseña", submit:"Guardar la contraseña", saving:"Guardando…", success:"¡Contraseña actualizada! Has iniciado sesión.", mismatch:"Las dos contraseñas no coinciden.", weak:"Contraseña demasiado débil: falta ", err:"No se pudo actualizar la contraseña. Inténtalo de nuevo.", expired:"Enlace caducado o no válido. Pide un nuevo correo desde « ¿Olvidaste tu contraseña? ».", reqLength:"8 caracteres", reqUpper:"una mayúscula", reqLower:"una minúscula", reqDigit:"un número", reqSpecial:"un carácter especial" },
  pt: { title:"Nova senha", intro:"Escolhe uma nova senha para a tua conta.", newL:"Nova senha", confL:"Confirma a senha", submit:"Guardar a senha", saving:"A guardar…", success:"Senha atualizada! Sessão iniciada.", mismatch:"As duas senhas não coincidem.", weak:"Senha demasiado fraca: falta ", err:"Não foi possível atualizar a senha. Tenta novamente.", expired:"Link expirado ou inválido. Pede um novo email em « Esqueceu a senha? ».", reqLength:"8 caracteres", reqUpper:"uma maiúscula", reqLower:"uma minúscula", reqDigit:"um dígito", reqSpecial:"um carácter especial" },
  nl: { title:"Nieuw wachtwoord", intro:"Kies een nieuw wachtwoord voor je account.", newL:"Nieuw wachtwoord", confL:"Bevestig het wachtwoord", submit:"Het wachtwoord opslaan", saving:"Bezig met opslaan…", success:"Wachtwoord bijgewerkt! Je bent aangemeld.", mismatch:"De twee wachtwoorden komen niet overeen.", weak:"Wachtwoord te zwak: er ontbreekt ", err:"Het wachtwoord kon niet bijgewerkt worden. Probeer opnieuw.", expired:"Link vervallen of ongeldig. Vraag een nieuwe e-mail aan via « Wachtwoord vergeten? ».", reqLength:"8 tekens", reqUpper:"een hoofdletter", reqLower:"een kleine letter", reqDigit:"een cijfer", reqSpecial:"een speciaal teken" },
};

export function ResetPasswordScreen({ onDone, showToast, lang="fr" }) {
  const rp = RP_I18N[lang] || RP_I18N.fr;
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const handle = async () => {
    const check = validatePassword(pwd);
    if (!check.valid) {
      const missing = [];
      if (!check.length) missing.push(rp.reqLength);
      if (!check.upper) missing.push(rp.reqUpper);
      if (!check.lower) missing.push(rp.reqLower);
      if (!check.digit) missing.push(rp.reqDigit);
      if (!check.special) missing.push(rp.reqSpecial);
      showToast({ type:"error", text: rp.weak + missing.join(", ") });
      return;
    }
    if (pwd !== pwd2) { showToast({ type:"error", text: rp.mismatch }); return; }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pwd });
      if (error) {
        setLoading(false);
        const m = (error.message || "").toLowerCase();
        if (m.includes("session") || m.includes("expired") || m.includes("jwt") || m.includes("missing")) showToast({ type:"error", text: rp.expired });
        else showToast({ type:"error", text: rp.err });
        return;
      }
      setLoading(false);
      showToast({ type:"success", text: rp.success });
      if (onDone) onDone();
    } catch (e) {
      setLoading(false);
      showToast({ type:"error", text: rp.err });
    }
  };
  return (
    <div style={{ minHeight:"100vh", background:"radial-gradient(ellipse 80% 60% at 50% -10%, rgba(255,180,120,.08) 0%, transparent 50%), radial-gradient(ellipse at top, #3B1F6E 0%, #1F1A4A 28%, #14132E 58%, #08081A 100%)", color:"#fff", display:"flex", flexDirection:"column" }}>
      <G/>
      <div style={{ padding:"20px 18px 10px", display:"flex", alignItems:"center", gap:12 }}>
        <img src="/horseconnex_logo_sticker_rouge.png" alt="" style={{ width:36, height:36, objectFit:"contain", filter:"drop-shadow(0 4px 12px rgba(200,0,26,.5))" }}/>
        <div style={{ fontFamily:"var(--fh)", fontSize:20, fontWeight:900, letterSpacing:.3 }}>{rp.title}</div>
      </div>
      <div style={{ flex:1, padding:"6px 18px 110px", display:"flex", flexDirection:"column", gap:14 }}>
        <div style={{ fontSize:13, color:"rgba(255,255,255,.7)", lineHeight:1.5 }}>{rp.intro}</div>
        <div style={{ background:"#fff", borderRadius:16, padding:18, boxShadow:"0 12px 30px rgba(0,0,0,.4)", display:"flex", flexDirection:"column", gap:8 }}>
          <Field label={rp.newL} type="password" value={pwd} onChange={e=>setPwd(e.target.value)} autoComplete="new-password" name="new-password"/>
          <Field label={rp.confL} type="password" value={pwd2} onChange={e=>setPwd2(e.target.value)} autoComplete="new-password" name="confirm-password"/>
        </div>
      </div>
      <div style={{ position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)", width:"100%", maxWidth:430, background:"rgba(13,13,26,.85)", backdropFilter:"blur(14px)", borderTop:"1px solid rgba(255,255,255,.08)", padding:"12px 20px 18px", zIndex:5 }}>
        <PrimaryButton onClick={handle} loading={loading}>{loading ? rp.saving : rp.submit}</PrimaryButton>
      </div>
    </div>
  );
}


// === LoginScreen ===
export function LoginScreen({onLogin,onBack,showToast,setUserCats,navigate,lang="fr"}) {
  const t = useLang(lang);
  const [captchaTokenLogin, setCaptchaTokenLogin] = useState(null);
  const [captchaFailed, setCaptchaFailed] = useState(false);
  const captchaRefLogin = useRef(null);
  const captchaWidgetLogin = useRef(null);
  
  useEffect(() => {
    let tries = 0;
    const interval = setInterval(() => {
      tries++;
      if (window.hcaptcha && captchaRefLogin.current && captchaWidgetLogin.current === null) {
        try {
          captchaWidgetLogin.current = window.hcaptcha.render(captchaRefLogin.current, {
            sitekey: "82d6f3d6-3b54-4220-854e-7f663662e8fa",
            size: "compact",
            callback: (token) => setCaptchaTokenLogin(token),
            "expired-callback": () => setCaptchaTokenLogin(null),
            "error-callback": () => setCaptchaTokenLogin(null)
          });
          clearInterval(interval);
        } catch(e) { setCaptchaFailed(true); clearInterval(interval); }
      } else if (tries >= 30) { setCaptchaFailed(true); clearInterval(interval); }
    }, 200);
    return () => clearInterval(interval);
  }, []);
  
  const [email,setEmail]=useState(""); const [pwd,setPwd]=useState(""); const [stay,setStay]=useState(false); const [loading,setLoading]=useState(false);
  // Compte créé mais email jamais confirmé (code OTP non saisi) : on renvoie un code et on
  // affiche la saisie ici même, sinon la personne est enfermée dehors (toast "confirme ton
  // email" sans aucun moyen d'obtenir un nouveau code hors de l'écran d'inscription).
  const [unconfirmed,setUnconfirmed]=useState(false); const [otp,setOtp]=useState(""); const [resendIn,setResendIn]=useState(0);
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn(s => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);
  // Pré-remplir l'email si on arrive ici depuis l'écran "compte confirmé"
  useEffect(() => {
    try {
      const pre = localStorage.getItem("hc_prefill_email");
      if (pre) { setEmail(pre); localStorage.removeItem("hc_prefill_email"); }
    } catch(e) {}
  }, []);
  const handle=async()=>{
    if(!email){showToast({type:"error",text:t.errEmailRequired});return;}
    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if(!emailRx.test(email)){showToast({type:"error",text:t.errEmailInvalid});return;}
    if(!pwd){showToast({type:"error",text:t.errPwdRequired});return;}
    setLoading(true);

    // Connexion via Supabase (admin et utilisateurs normaux)
    try {
      if (!captchaTokenLogin) {
        showToast({type:"error",text:captchaFailed?t.errCaptchaBlocked:t.errCaptcha});
        setLoading(false);
        return;
      }
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: pwd,
        options: { captchaToken: captchaTokenLogin }
      });

      if (error) {
        setLoading(false);
        // Jeton hCaptcha à usage unique : sans reset, le 2e essai échoue sur le captcha.
        try { if (window.hcaptcha && captchaWidgetLogin.current !== null) window.hcaptcha.reset(captchaWidgetLogin.current); } catch (e) {}
        setCaptchaTokenLogin(null);
        if (error.message.includes("Invalid")) {
          showToast({type:"error",text:t.errLoginIncorrect});
        } else if (error.message.includes("Email not confirmed")) {
          setOtp(""); setUnconfirmed(true);
          // Ne jamais annoncer « code envoyé » sans l'avoir vérifié : c'est ce que voyaient les
          // comptes bloqués avant le 24 sept., alors que le renvoi échouait sur le captcha.
          const r = await resendSignupCode(email);
          if (r.ok) { setResendIn(60); showToast({type:"info",text:t.unconfirmedCodeSent}); }
          else { setResendIn(0); showToast({type:"error",text:resendErrorText(r, t)}); }
        } else {
          showToast({type:"error",text:t.errPrefix + error.message});
        }
        return;
      }

      // Vérifier si c'est l'admin
      const isAdmin = data.user.email && data.user.email.toLowerCase() === ADMIN_EMAIL.toLowerCase();

      // Charger le profil depuis la base de données
      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", data.user.id)
        .single();

      if (profile) {
        if(setUserCats) setUserCats(isAdmin ? ["Admin"] : (profile.categories || []));
      } else if (isAdmin) {
        if(setUserCats) setUserCats(["Admin"]);
      }

      showToast({type:"success",text: isAdmin ? t.loginAdminSuccess : t.loginSuccess});
      setTimeout(()=>onLogin(isAdmin),500);
    } catch(err) {
      setLoading(false);
      showToast({type:"error",text:t.errServer});
      console.error(err);
    }
  };
  const verifyLoginCode = async () => {
    const code = (otp || "").replace(/\s/g, "");
    if (code.length < 6) { showToast({type:"error",text:t.errCodeInvalid}); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.verifyOtp({ email, token: code, type: "signup" });
      if (error || !data?.session) { setLoading(false); showToast({type:"error",text:t.errCodeWrong}); return; }
      trackSignupEvent("email_confirmed", lang);
      const isAdmin = data.user?.email && data.user.email.toLowerCase() === ADMIN_EMAIL.toLowerCase();
      const { data: profile } = await supabase.from("profiles").select("*").eq("id", data.user.id).single();
      if (setUserCats) setUserCats(isAdmin ? ["Admin"] : (profile?.categories || []));
      showToast({type:"success",text:t.emailConfirmedWelcome});
      setTimeout(()=>onLogin(!!isAdmin),500);
    } catch(err) {
      setLoading(false);
      showToast({type:"error",text:t.errConnection});
      console.error(err);
    }
  };
  const resendLoginCode = async () => {
    if (resendIn > 0) return;
    try {
      const r = await resendSignupCode(email);
      if (!r.ok) { showToast({type:"error",text:resendErrorText(r, t)}); return; }
      setResendIn(60);
      showToast({type:"success",text:t.codeResent});
    } catch(err) { showToast({type:"error",text:t.errConnection}); }
  };
  if (unconfirmed) return (
    <div style={{
      minHeight: "100vh",
      background: "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(255,180,120,.08) 0%, transparent 50%), radial-gradient(ellipse at top, #3B1F6E 0%, #1F1A4A 28%, #14132E 58%, #08081A 100%)",
      color: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: 24, position: "relative", overflow: "hidden",
    }}><G/>
      <AuthSparkles />
      <div style={{textAlign:"center",maxWidth:360,width:"100%",position:"relative",zIndex:1}}>
        <div style={{width:80,height:80,borderRadius:"50%",background:"rgba(34,197,94,.15)",border:"1px solid rgba(34,197,94,.35)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px",boxShadow:"0 0 30px rgba(34,197,94,.3)"}}>
          <CIcon name="mail" s={36} c="#22c55e"/>
        </div>
        <div style={{fontFamily:"var(--fh)",fontSize:24,fontWeight:900,color:"#fff",marginBottom:10,letterSpacing:-0.3}}>{t.enterCodeTitle}</div>
        <div style={{fontSize:13,color:"rgba(255,255,255,.65)",lineHeight:1.7,marginBottom:6}}>{t.codeSentTo}</div>
        <div style={{fontSize:14,fontWeight:700,color:"#FF5A6E",marginBottom:18,wordBreak:"break-all"}}>{email}</div>
        <input
          value={otp}
          onChange={e=>setOtp(e.target.value.replace(/[^0-9]/g,"").slice(0,8))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="••••••••"
          style={{
            width:"100%", boxSizing:"border-box", textAlign:"center", fontSize:26, fontWeight:800,
            letterSpacing:8, fontFamily:"var(--fh)", color:"#fff",
            background:"rgba(255,255,255,.06)", border:"1.5px solid rgba(255,255,255,.2)",
            borderRadius:14, padding:"14px 10px", marginBottom:16, outline:"none",
          }}
        />
        <PrimaryButton onClick={verifyLoginCode} loading={loading} disabled={otp.length<6}>{t.verifyCodeBtn}</PrimaryButton>
        <button onClick={resendLoginCode} disabled={resendIn>0} style={{display:"block",margin:"16px auto 0",background:"none",border:"none",color:resendIn>0?"rgba(255,255,255,.4)":"#22c55e",fontSize:13,fontWeight:700,cursor:resendIn>0?"default":"pointer",textDecoration:resendIn>0?"none":"underline"}}>
          {resendIn>0 ? (t.resendIn+" "+resendIn+"s") : t.resendCode}
        </button>
        <div style={{fontSize:12.5,color:"rgba(255,255,255,.8)",marginTop:12,lineHeight:1.55,background:"rgba(255,255,255,.06)",border:"1px solid rgba(255,255,255,.12)",borderRadius:12,padding:"9px 12px"}}>{t.checkSpam}</div>
        <button onClick={()=>{setUnconfirmed(false);setOtp("");}} style={{display:"block",margin:"14px auto 0",background:"none",border:"none",color:"rgba(255,255,255,.5)",fontSize:12,cursor:"pointer",textDecoration:"underline"}}>{t.backToLogin}</button>
      </div>
    </div>
  );
  return (
    <div style={{
      minHeight: "100vh",
      background: "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(255,180,120,.08) 0%, transparent 50%), radial-gradient(ellipse at top, #3B1F6E 0%, #1F1A4A 28%, #14132E 58%, #08081A 100%)",
      color: "#fff",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      overflow: "hidden",
    }}>
      <G/>
      <AuthSparkles />

      {/* Header glassmorphism dark sticky */}
      <div style={{
        position: "sticky", top: 0, zIndex: 10,
        padding: "16px 18px 14px",
        background: "rgba(13,13,26,.55)",
        backdropFilter: "blur(14px)",
        borderBottom: "1px solid rgba(255,255,255,.06)",
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <motion.button
          whileTap={{ scale: 0.92 }}
          onClick={onBack}
          style={{
            background: "rgba(255,255,255,.08)",
            border: "1px solid rgba(255,255,255,.12)",
            borderRadius: 10,
            width: 36, height: 36,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", flexShrink: 0,
          }}
        >
          <CIcon name="back" s={18} c="#fff"/>
        </motion.button>
        <div style={{ fontFamily: "var(--fh)", fontSize: 18, fontWeight: 900, color: "#fff", letterSpacing: 0.3, flex: 1 }}>
          {t.login}
        </div>
        <img
          src="/horseconnex_logo_sticker_rouge.png"
          alt=""
          style={{
            width: 32, height: 32, objectFit: "contain",
            filter: "drop-shadow(0 4px 12px rgba(200,0,26,.5))",
          }}
        />
      </div>

      {/* Card blanche contenant les inputs (lisibilité maximale) */}
      <div style={{
        flex: 1, padding: "22px 18px 110px",
        display: "flex", flexDirection: "column", gap: 14,
        position: "relative", zIndex: 1,
      }}>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          style={{
            background: "#fff",
            borderRadius: 16,
            padding: 18,
            boxShadow: "0 12px 30px rgba(0,0,0,.4), 0 0 0 1px rgba(255,255,255,.05)",
            display: "flex", flexDirection: "column", gap: 12,
          }}
        >
          <Field label={t.email} placeholder={t.emailPh||"votre@email.com"} type="email" icon="mail" value={email} onChange={e=>setEmail(e.target.value)} required autoComplete="email" inputMode="email" name="email"/>
          <Field label={t.password} placeholder="••••••••" type="password" icon="lock" value={pwd} onChange={e=>setPwd(e.target.value)} required autoComplete="current-password" name="password"/>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:2}}>
            <Chk on={stay} toggle={()=>setStay(!stay)} label={t.stayConnected}/>
            <button onClick={()=>navigate("forgotPassword")} style={{background:"none",border:"none",color:"var(--red)",fontSize:12.5,fontWeight:600,cursor:"pointer",padding:0}}>{t.forgotPassword}</button>
          </div>
        </motion.div>

        <div ref={captchaRefLogin} style={{display:"flex",justifyContent:"center",margin:"4px 0"}}/>
        {captchaFailed && (
          <div style={{color:"#FF5A6E",fontSize:12.5,lineHeight:1.4,textAlign:"center",padding:"0 14px"}}>
            {t.errCaptchaBlocked}
          </div>
        )}
      </div>

      {/* Footer fixé glassmorphism dark + bouton gradient rouge + shine */}
      <div style={{
        position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)",
        width: "100%", maxWidth: 430,
        background: "rgba(13,13,26,.85)",
        backdropFilter: "blur(14px)",
        borderTop: "1px solid rgba(255,255,255,.08)",
        padding: "12px 20px 18px",
        zIndex: 5,
      }}>
        <PrimaryButton onClick={handle} loading={loading}>{loading ? "..." : t.login}</PrimaryButton>
      </div>
    </div>
  );
}


// === RegisterScreen ===
export function RegisterScreen({onRegister,onBack,showToast,lang="fr",setUser,navigate,completeProfile=false,googleUser=null}) {
  const t = useLang(lang);
  const catLabel = (c) => (USER_CATS_I18N[c] && USER_CATS_I18N[c][lang]) || c;
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaFailed, setCaptchaFailed] = useState(false);
  const captchaRef = useRef(null);
  const captchaWidgetRef = useRef(null);

  // Initialiser le widget hCaptcha quand le composant est monte.
  // En mode "complète profil" (login Google), pas de captcha : on saute.
  useEffect(() => {
    if (completeProfile) return;
    let tries = 0;
    const interval = setInterval(() => {
      tries++;
      if (window.hcaptcha && captchaRef.current && captchaWidgetRef.current === null) {
        try {
          captchaWidgetRef.current = window.hcaptcha.render(captchaRef.current, {
            sitekey: "82d6f3d6-3b54-4220-854e-7f663662e8fa",
            size: "compact",
            callback: (token) => setCaptchaToken(token),
            "expired-callback": () => setCaptchaToken(null),
            "error-callback": () => setCaptchaToken(null)
          });
          clearInterval(interval);
        } catch(e) {
          console.error("hCaptcha render error:", e);
          setCaptchaFailed(true);
          clearInterval(interval);
        }
      } else if (tries >= 30) { setCaptchaFailed(true); clearInterval(interval); }
    }, 200);
    return () => clearInterval(interval);
  }, [completeProfile]);

  const [cats,setCats]=useState([]); const [open,setOpen]=useState(false); const [showInfo,setShowInfo]=useState(false);
  // Nouvelle taxonomie : profil (particulier/pro). null tant qu'on n'a pas choisi.
  const [profileType,setProfileType]=useState(null);
  const [form,setForm]=useState({prenom:"",nom:"",email:"",pwd:"",pwdConf:"",tel:"",ville:"",pays:"",naissance:"",nomEcurie:"",nomPro:"",autreActivite:"",showTel:false,showAdresse:false,contactPhone:true,contactEmail:false,contactMessage:true});
  const [loading,setLoading]=useState(false);
  const [stayReg,setStayReg]=useState(false);
  const [acceptedTerms,setAcceptedTerms]=useState(false);
  const [emailSent,setEmailSent]=useState(false);
  const [otp,setOtp]=useState("");
  const [resendIn,setResendIn]=useState(0);
  // Date de naissance via 3 menus (jour/mois/année) : le sélecteur date natif
  // est capricieux en WebView/TWA (année bloquée). On reconstruit "AAAA-MM-JJ".
  const _initB = (form.naissance && /^\d{4}-\d{2}-\d{2}$/.test(form.naissance)) ? form.naissance.split("-") : ["","",""];
  const [bYear,setBYear]=useState(_initB[0]);
  const [bMonth,setBMonth]=useState(_initB[1]);
  const [bDay,setBDay]=useState(_initB[2]);
  // Barrière d'âge (Texas SB 2420 / App Store Accountability + RGPD/COPPA) :
  // âge depuis "AAAA-MM-JJ" (input type=date) ou "JJ/MM/AAAA". null si illisible.
  const ageFromBirthdate = (s) => {
    if (!s) return null;
    let y, m, d;
    if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(s)) [y, m, d] = s.split("-").map(Number);
    else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) [d, m, y] = s.split("/").map(Number);
    else return null;
    if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900) return null;
    const now = new Date();
    let age = now.getFullYear() - y;
    if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)) age--;
    return age;
  };
  // Tunnel d'inscription (B) : le visiteur a ouvert l'écran d'inscription
  useEffect(() => { trackSignupEvent("opened", lang); }, []);
  // Décompte du bouton "Renvoyer le code" (anti-spam, 60s)
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn(s => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);
  const toggle=c=>setCats(p=>p.includes(c)?p.filter(x=>x!==c):[...p,c]);
  const up=(k,v)=>setForm(f=>({...f,[k]:v}));
  const onBirth=(part,val)=>{
    const y=part==="y"?val:bYear, m=part==="m"?val:bMonth, d=part==="d"?val:bDay;
    if(part==="y")setBYear(val); else if(part==="m")setBMonth(val); else setBDay(val);
    up("naissance",(y&&m&&d)?`${y}-${m}-${d}`:"");
  };
  // Mode "complète profil" (1er login Google) : pré-remplir prénom/nom/email
  // depuis l'identité Google. L'email et le mot de passe viennent de Google,
  // donc on ne les édite pas (champs masqués + captcha sauté).
  useEffect(() => {
    if (!completeProfile || !googleUser) return;
    const meta = googleUser.user_metadata || {};
    const fullName = meta.full_name || meta.name || "";
    let prenom = meta.given_name || meta.first_name || "";
    let nom = meta.family_name || meta.last_name || "";
    if ((!prenom || !nom) && fullName) {
      const parts = fullName.trim().split(/\s+/);
      if (!prenom) prenom = parts[0] || "";
      if (!nom) nom = parts.slice(1).join(" ") || "";
    }
    setForm(f => ({ ...f, prenom: f.prenom || prenom, nom: f.nom || nom, email: googleUser.email || f.email }));
  }, [completeProfile, googleUser]);
  const handle=async()=>{
    if(completeProfile){
      // Inscription via Google : on saute email/mot de passe/captcha.
      // On garde : CGU, catégories, prénom/nom. Téléphone + ville = optionnels.
      if(!acceptedTerms){showToast({type:"error",text:t.errMustAcceptTerms});return;}
      if(!cats.length){showToast({type:"error",text:t.selectAtLeastOne});return;}
      if(!form.prenom||!form.nom||form.prenom.trim().length<2||form.nom.trim().length<2){showToast({type:"error",text:t.errNameRequired});return;}
      { const age=ageFromBirthdate(form.naissance);
        if(age===null||age>120){showToast({type:"error",text:t.errBirthdateRequired});return;}
        if(age<13){ trackSignupEvent("blocked_underage", lang); alert(t.errTooYoung); try{await supabase.auth.signOut();}catch(e){} if(navigate)navigate("auth"); return; } }
      setLoading(true);
      try {
        const { error } = await supabase.from("profiles").update({
          prenom: form.prenom.trim(),
          nom: form.nom.trim(),
          telephone: form.tel || "",
          adresse: form.ville || "",
          pays: form.pays || "",
          country: countryCode(form.pays) || null,
          categories: cats,
          show_telephone: form.showTel || false,
          show_adresse: form.showAdresse || false,
          contact_phone: form.contactPhone !== false,
          contact_email: form.contactEmail || false,
          contact_message: form.contactMessage !== false,
          nom_ecurie: form.nomEcurie || "",
          nom_pro: form.nomPro || "",
        }).eq("id", googleUser?.id);
        if (error) {
          setLoading(false);
          showToast({type:"error",text:t.errPrefix + error.message});
          return;
        }
        if (setUser) setUser(u => ({
          ...(u || {}),
          id: googleUser?.id,
          prenom: form.prenom.trim(), nom: form.nom.trim(),
          name: form.prenom.trim()+" "+form.nom.trim(),
          email: googleUser?.email || form.email,
          tel: form.tel||"", ville: form.ville||"", pays: form.pays||"", country: countryCode(form.pays),
          cats: cats, showTel: form.showTel||false, showAdresse: form.showAdresse||false,
          contactPhone: form.contactPhone!==false, contactEmail: form.contactEmail||false, contactMessage: form.contactMessage!==false,
          nomEcurie: form.nomEcurie||"", nomPro: form.nomPro||"",
        }));
        // Consentement cookies enregistré ici (le bloc CGU couvre les cookies) :
        // plus besoin du popup cookies au démarrage. Vaut aussi pour le 1er login Google.
        try { localStorage.setItem("hc_cookies_consent", JSON.stringify({ analytics:true, essential:true, source:"signup_google", ts:Date.now() })); } catch(e){}
        setLoading(false);
        onRegister(cats);
      } catch(err) {
        setLoading(false);
        showToast({type:"error",text:t.errServer});
        console.error(err);
      }
      return;
    }
    if(!acceptedTerms){showToast({type:"error",text:t.errMustAcceptTerms});return;}
    if(!captchaToken){showToast({type:"error",text:captchaFailed?t.errCaptchaBlocked:t.errCaptchaBeforeContinue});return;}
    if(!cats.length){showToast({type:"error",text:t.selectAtLeastOne});return;}
    if(!form.prenom||!form.nom||form.prenom.trim().length<2||form.nom.trim().length<2){showToast({type:"error",text:t.errNameRequired});return;}
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if(!form.email||!emailRegex.test(form.email)){showToast({type:"error",text:t.errEmailInvalid});return;}
    const pwdCheck = validatePassword(form.pwd);
    if(!pwdCheck.valid){
      let missing = [];
      if(!pwdCheck.length) missing.push(t.pwdReqLength);
      if(!pwdCheck.upper) missing.push(t.pwdReqUpper);
      if(!pwdCheck.lower) missing.push(t.pwdReqLower);
      if(!pwdCheck.digit) missing.push(t.pwdReqDigit);
      if(!pwdCheck.special) missing.push(t.pwdReqSpecial);
      showToast({type:"error",text:t.pwdPrefix+missing.join(", ")});
      return;
    }
    // Confirmation mot de passe retiree (l'oeil "afficher" du champ suffit a eviter
    // les fautes de frappe). Telephone + ville optionnels (reduction friction
    // inscription) -> a completer plus tard dans le profil.
    { const age=ageFromBirthdate(form.naissance);
      if(age===null||age>120){showToast({type:"error",text:t.errBirthdateRequired});return;}
      if(age<13){ trackSignupEvent("blocked_underage", lang); alert(t.errTooYoung); return; } }
    // Consentement cookies enregistré une fois toutes les validations passées (le bloc
    // CGU couvre les cookies) -> plus besoin du popup cookies au démarrage.
    try { localStorage.setItem("hc_cookies_consent", JSON.stringify({ analytics:true, essential:true, source:"signup_email", ts:Date.now() })); } catch(e){}
    // Inscriptions fermées depuis l'Admin : on vérifie juste avant la création,
    // sur la valeur en base et non sur un état chargé au démarrage.
    try {
      const s = await getAppSettings();
      if (s && s.signups_open === false) { showToast({type:"error",text:t.signupsClosed}); return; }
    } catch(e) { /* une panne de lecture ne doit pas bloquer une inscription */ }
    setLoading(true);
    try {
      // Créer le compte avec Supabase
      const { data, error } = await supabase.auth.signUp({
        email: form.email,
        password: form.pwd,
        options: {
          captchaToken,
          data: {
            prenom: form.prenom.trim(),
            nom: form.nom.trim(),
            telephone: form.tel || "",
            adresse: form.ville || "",
            pays: form.pays || "",
            country: countryCode(form.pays) || null,
            categories: cats,
            show_telephone: form.showTel || false,
            show_adresse: form.showAdresse || false,
            contact_phone: form.contactPhone !== false,
            contact_email: form.contactEmail || false,
            contact_message: form.contactMessage !== false,
            nom_ecurie: form.nomEcurie || "",
            nom_pro: form.nomPro || "",
            naissance: form.naissance || "",
            lang: lang,
          },
          emailRedirectTo: window.location.origin
        }
      });

      if (error) {
        setLoading(false);
        try { if (window.hcaptcha && captchaWidgetRef.current !== null) window.hcaptcha.reset(captchaWidgetRef.current); } catch (e) {}
        setCaptchaToken(null);
        // Gérer les erreurs courantes
        if (error.message.includes("already registered")) {
          showToast({type:"error",text:t.errEmailTaken});
        } else if (error.message.includes("weak")) {
          showToast({type:"error",text:t.errPwdWeak});
        } else {
          showToast({type:"error",text:t.errPrefix + error.message});
        }
        return;
      }

      // Tunnel d'inscription (B) : compte effectivement créé
      trackSignupEvent("account_created", lang);
      // Succès inscription - juste afficher l'écran de vérification email
      // NE PAS setUser tant que l'email n'est pas confirmé !
      setEmailSent(true);
      setResendIn(60);
      setLoading(false);
    } catch(err) {
      setLoading(false);
      showToast({type:"error",text:t.errServer});
      console.error(err);
    }
  };

  // Vérifie le code OTP reçu par email (alternative au lien de confirmation).
  const verifyCode = async () => {
    const code = (otp || "").replace(/\s/g, "");
    if (code.length < 6) { showToast({type:"error",text:t.errCodeInvalid || "Entre le code reçu par email."}); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.verifyOtp({ email: form.email, token: code, type: "signup" });
      if (error || !data?.session) {
        setLoading(false);
        showToast({type:"error",text:t.errCodeWrong || "Code invalide ou expiré. Réessaie ou renvoie un code."});
        return;
      }
      // Tunnel d'inscription (B) : email confirme (code OTP valide) -> compte vraiment
      // actif. Permet de mesurer le drop entre "formulaire soumis" et "email confirme".
      trackSignupEvent("email_confirmed", lang);
      if (setUser) setUser({
        prenom: form.prenom.trim(), nom: form.nom.trim(),
        name: form.prenom.trim()+" "+form.nom.trim(),
        email: form.email, tel: form.tel||"",
        ville: form.ville||"", pays: form.pays||"", country: countryCode(form.pays),
        bio: "", avatar: null, badge: null, verified: false,
        cats: cats, showTel: form.showTel||false, showAdresse: form.showAdresse||false,
        contactPhone: form.contactPhone!==false, contactEmail: form.contactEmail||false, contactMessage: form.contactMessage!==false,
        nomEcurie: form.nomEcurie||"", nomPro: form.nomPro||"",
      });
      showToast({type:"success",text:t.emailConfirmedWelcome});
      onRegister(cats);
    } catch(err) {
      setLoading(false);
      showToast({type:"error",text:t.errConnection});
      console.error(err);
    }
  };

  // Renvoie un nouveau code par email (anti-spam : décompte 60s).
  const resendCode = async () => {
    if (resendIn > 0) return;
    try {
      const r = await resendSignupCode(form.email);
      if (!r.ok) { showToast({type:"error",text:resendErrorText(r, t)}); return; }
      setResendIn(60);
      showToast({type:"success",text:t.codeResent || "Nouveau code envoyé !"});
    } catch(err) {
      showToast({type:"error",text:t.errConnection});
    }
  };

  // Écran confirmation email (saisie du code OTP) — thème nocturne.
  // Jamais affiché en mode "complète profil" (pas de signup email).
  if(emailSent && !completeProfile) return (
    <div style={{
      minHeight: "100vh",
      background: "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(255,180,120,.08) 0%, transparent 50%), radial-gradient(ellipse at top, #3B1F6E 0%, #1F1A4A 28%, #14132E 58%, #08081A 100%)",
      color: "#fff",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
      position: "relative",
      overflow: "hidden",
    }}><G/>
      <AuthSparkles />
      <div style={{textAlign:"center",maxWidth:360,width:"100%",position:"relative",zIndex:1}}>
        <div style={{width:80,height:80,borderRadius:"50%",background:"rgba(34,197,94,.15)",border:"1px solid rgba(34,197,94,.35)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px",boxShadow:"0 0 30px rgba(34,197,94,.3)"}}>
          <CIcon name="mail" s={36} c="#22c55e"/>
        </div>
        <div style={{fontFamily:"var(--fh)",fontSize:24,fontWeight:900,color:"#fff",marginBottom:10,letterSpacing:-0.3}}>{t.enterCodeTitle || "Vérifie ton email"}</div>
        <div style={{fontSize:13,color:"rgba(255,255,255,.65)",lineHeight:1.7,marginBottom:6}}>{t.codeSentTo || "On t'a envoyé un code par email à :"}</div>
        <div style={{fontSize:14,fontWeight:700,color:"#FF5A6E",marginBottom:18,wordBreak:"break-all"}}>{form.email}</div>
        <input
          value={otp}
          onChange={e=>setOtp(e.target.value.replace(/[^0-9]/g,"").slice(0,8))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="••••••••"
          style={{
            width:"100%", boxSizing:"border-box", textAlign:"center", fontSize:26, fontWeight:800,
            letterSpacing:8, fontFamily:"var(--fh)", color:"#fff",
            background:"rgba(255,255,255,.06)", border:"1.5px solid rgba(255,255,255,.2)",
            borderRadius:14, padding:"14px 10px", marginBottom:16, outline:"none",
          }}
        />
        <PrimaryButton onClick={verifyCode} loading={loading} disabled={otp.length<6}>
          {t.verifyCodeBtn || "Vérifier le code"}
        </PrimaryButton>
        <button onClick={resendCode} disabled={resendIn>0} style={{display:"block",margin:"16px auto 0",background:"none",border:"none",color:resendIn>0?"rgba(255,255,255,.4)":"#22c55e",fontSize:13,fontWeight:700,cursor:resendIn>0?"default":"pointer",textDecoration:resendIn>0?"none":"underline"}}>
          {resendIn>0 ? ((t.resendIn || "Renvoyer le code dans")+" "+resendIn+"s") : (t.resendCode || "Renvoyer le code")}
        </button>
        <div style={{fontSize:12.5,color:"rgba(255,255,255,.8)",marginTop:12,lineHeight:1.55,background:"rgba(255,255,255,.06)",border:"1px solid rgba(255,255,255,.12)",borderRadius:12,padding:"9px 12px"}}>{t.checkSpam}</div>
        <button onClick={()=>{setEmailSent(false);setOtp("");}} style={{display:"block",margin:"14px auto 0",background:"none",border:"none",color:"rgba(255,255,255,.5)",fontSize:12,cursor:"pointer",textDecoration:"underline"}}>{t.editMyEmail}</button>
      </div>
    </div>
  );

  return (
    <div style={{
      minHeight: "100vh",
      background: "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(255,180,120,.08) 0%, transparent 50%), radial-gradient(ellipse at top, #3B1F6E 0%, #1F1A4A 28%, #14132E 58%, #08081A 100%)",
      color: "#fff",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      overflow: "hidden",
    }}>
      <G/>
      <AuthSparkles />

      {/* Header glassmorphism dark */}
      <div style={{
        position: "sticky", top: 0, zIndex: 10,
        padding: "16px 18px 14px",
        background: "rgba(13,13,26,.55)",
        backdropFilter: "blur(14px)",
        borderBottom: "1px solid rgba(255,255,255,.06)",
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <motion.button
          whileTap={{ scale: 0.92 }}
          onClick={onBack}
          style={{
            background: "rgba(255,255,255,.08)",
            border: "1px solid rgba(255,255,255,.12)",
            borderRadius: 10,
            width: 36, height: 36,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", flexShrink: 0,
          }}
        >
          <CIcon name="back" s={18} c="#fff"/>
        </motion.button>
        <div style={{ fontFamily: "var(--fh)", fontSize: 18, fontWeight: 900, color: "#fff", letterSpacing: 0.3, flex: 1 }}>
          {completeProfile ? (t.completeProfileTitle || "Complète ton profil") : t.createAccount}
        </div>
        <img
          src="/horseconnex_logo_sticker_rouge.png"
          alt=""
          style={{
            width: 32, height: 32, objectFit: "contain",
            filter: "drop-shadow(0 4px 12px rgba(200,0,26,.5))",
          }}
        />
      </div>

      <div style={{flex:1,padding:"16px 18px 28px",overflowY:"auto",display:"flex",flexDirection:"column",gap:12, position:"relative", zIndex:1}}>
        {/* Catégories */}
        <div className="fu" style={{background:"#fff",borderRadius:14,padding:16,boxShadow:"var(--sh)",border:"1px solid var(--border)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:"var(--muted)",letterSpacing:1.5,textTransform:"uppercase"}}>🐴 {t.whoAmI} <span style={{color:"var(--red)"}}>*</span></div>
              <div style={{fontSize:11,color:"var(--muted)",marginTop:3}}>{t.multipleChoice}</div>
            </div>
            <div onClick={()=>setShowInfo(!showInfo)} style={{width:22,height:22,borderRadius:"50%",background:"var(--bg)",border:"1.5px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0,marginTop:2}}>
              <span style={{fontSize:12,fontWeight:700,color:"var(--muted)"}}>?</span>
            </div>
          </div>
          {showInfo && (
            <div style={{background:"rgba(200,0,26,.05)",border:"1px solid rgba(200,0,26,.15)",borderRadius:10,padding:"10px 12px",marginBottom:10,fontSize:12,color:"var(--text)",lineHeight:1.6}}>
              <strong style={{color:"var(--red)"}}>{t.whyImportant}</strong><br/>
              {t.whyImportantText}<br/><br/>
              <span style={{color:"var(--muted)"}}>{t.editLater}</span>
            </div>
          )}
          <IdentityPicker
            profile={profileType}
            selected={cats}
            lang={lang}
            dark={false}
            onChange={(p,s)=>{ setProfileType(p); setCats(s); }}
          />
          {cats.includes("ecurie_pension")&&(
            <div style={{marginTop:10}}>
              <Field label={t.stableName} placeholder={t.stableNamePh} icon="stables" value={form.nomEcurie||""} onChange={e=>up("nomEcurie",e.target.value)} required/>
            </div>
          )}
          {profileType==="pro" && cats.length>0 && !cats.includes("ecurie_pension") &&(
            <div style={{marginTop:10}}>
              <Field label={t.companyName} placeholder={t.companyNamePh} icon="edit" value={form.nomPro||""} onChange={e=>up("nomPro",e.target.value)}/>
            </div>
          )}
        </div>
        {/* Infos perso */}
        <div className="fu1" style={{background:"#fff",borderRadius:14,padding:16,boxShadow:"var(--sh)",border:"1px solid var(--border)"}}>
          <div style={{fontSize:10,fontWeight:700,color:"var(--muted)",letterSpacing:1.5,textTransform:"uppercase",marginBottom:12}}>👤 {t.personalInfo}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9,marginBottom:10}}>
            <Field label={t.firstName} placeholder="Marie" value={form.prenom} onChange={e=>up("prenom",e.target.value)} required autoComplete="given-name"/>
            <Field label={t.lastName} placeholder="Dupont" value={form.nom} onChange={e=>up("nom",e.target.value)} required autoComplete="family-name"/>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {!completeProfile && (
              <Field label={t.email} placeholder={t.emailPh||"votre@email.com"} type="email" icon="mail" value={form.email} onChange={e=>up("email",e.target.value)} required autoComplete="email" inputMode="email" name="email"/>
            )}
            {!completeProfile && (
              <Field label={t.password} placeholder={t.pwdPlaceholder} type="password" icon="lock" value={form.pwd} onChange={e=>up("pwd",e.target.value)} required showStrength autoComplete="new-password" name="new-password"/>
            )}
            <Field label={t.phone} placeholder="+41 00 000 00 00" type="tel" icon="phone" value={form.tel||""} onChange={e=>up("tel",e.target.value)} autoComplete="tel" inputMode="tel" name="tel"/>
            <Field label={t.address} placeholder={t.addressPlaceholder} icon="pin" value={form.ville} onChange={e=>up("ville",e.target.value)} autoComplete="street-address"/>
            <Field label={t.country} placeholder="Suisse" icon="globe" value={form.pays} onChange={e=>up("pays",e.target.value)} autoComplete="country-name"/>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:"var(--muted)",letterSpacing:1.5,textTransform:"uppercase",marginBottom:5}}>
                {t.birthdate}<span style={{color:"var(--red)",marginLeft:2}}>*</span>
              </div>
              <div style={{display:"flex",gap:8}}>
                {(()=>{
                  const sx={flex:1,padding:"11px 8px",background:"#fff",border:"1.5px solid var(--border)",borderRadius:10,fontSize:13,color:"var(--text)",fontFamily:"var(--fb)",WebkitAppearance:"none",boxSizing:"border-box"};
                  const cy=new Date().getFullYear();
                  const pad=n=>String(n).padStart(2,"0");
                  const days=[];for(let d=1;d<=31;d++)days.push(d);
                  const months=[];for(let m=1;m<=12;m++)months.push(m);
                  const years=[];for(let y=cy;y>=1920;y--)years.push(y);
                  return (<>
                    <select value={bDay} onChange={e=>onBirth("d",e.target.value)} style={sx}>
                      <option value="">{t.bDay}</option>
                      {days.map(d=><option key={d} value={pad(d)}>{d}</option>)}
                    </select>
                    <select value={bMonth} onChange={e=>onBirth("m",e.target.value)} style={sx}>
                      <option value="">{t.bMonth}</option>
                      {months.map(m=><option key={m} value={pad(m)}>{pad(m)}</option>)}
                    </select>
                    <select value={bYear} onChange={e=>onBirth("y",e.target.value)} style={{...sx,flex:1.3}}>
                      <option value="">{t.bYear}</option>
                      {years.map(y=><option key={y} value={String(y)}>{y}</option>)}
                    </select>
                  </>);
                })()}
              </div>
            </div>
          </div>

          {/* Consentement CGU + Confidentialité (RGPD : explicite, lié au compte) */}
          <div style={{
            marginTop: 16,
            padding: "12px 14px",
            background: acceptedTerms ? "rgba(4,120,87,.05)" : "rgba(200,0,26,.04)",
            border: `1px solid ${acceptedTerms ? "rgba(4,120,87,.3)" : "rgba(200,0,26,.18)"}`,
            borderRadius: 10,
            transition: "background .2s, border-color .2s",
          }}>
            <Chk on={acceptedTerms} toggle={()=>setAcceptedTerms(!acceptedTerms)} label={t.acceptTerms}/>
            <div style={{ display: "flex", gap: 14, marginTop: 8, marginLeft: 30, flexWrap: "wrap" }}>
              <a href={legalUrl("terms", lang)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "var(--red)", textDecoration: "underline", textUnderlineOffset: 2, fontWeight: 600 }}>
                📄 {t.readTerms}
              </a>
              <a href={legalUrl("privacy", lang)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "var(--red)", textDecoration: "underline", textUnderlineOffset: 2, fontWeight: 600 }}>
                🔒 {t.readPrivacy}
              </a>
              <a href={legalUrl("privacy", lang)+"#cookies"} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "var(--red)", textDecoration: "underline", textUnderlineOffset: 2, fontWeight: 600 }}>
                🍪 Cookies
              </a>
            </div>
          </div>
        </div>
        {!completeProfile && <div ref={captchaRef} style={{display:"flex",justifyContent:"center",margin:"20px 0 14px 0"}}/>}
        {!completeProfile && captchaFailed && <div style={{color:"var(--red)",fontSize:12.5,lineHeight:1.4,textAlign:"center",padding:"0 14px"}}>{t.errCaptchaBlocked}</div>}
        {/* Footer EN FLUX (plus fixe) : le bouton est en bas de page et ne couvre plus l'écran */}
        <div style={{
          marginTop: 6,
          background: "rgba(255,255,255,.05)",
          borderRadius: 14,
          border: "1px solid rgba(255,255,255,.1)",
          padding: "14px 16px 16px",
        }}>
          <div style={{ marginBottom: 10 }}>
            <Chk on={stayReg} toggle={()=>setStayReg(!stayReg)} label={t.stayConnected} dark/>
          </div>
          <PrimaryButton onClick={handle} loading={loading} disabled={!acceptedTerms}>
            {completeProfile
              ? (loading ? t.creatingAccount : (t.completeProfileBtn || "Terminer mon profil ✓"))
              : (loading ? t.creatingAccount : t.createAccount + " ✓")}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

// === ContactSAVScreen ===
export function ContactSAVScreen({onBack, showToast, lang="fr"}) {
  const t = useLang(lang);
  const [form,setForm]=useState({sujet:"",message:"",email:""});
  const [sent,setSent]=useState(false);
  const up=(k,v)=>setForm(f=>({...f,[k]:v}));

const sujets=[t.subjTechnical,t.subjReport,t.subjAccount,t.subjAds,t.subjRefund,t.subjOther];

  const handle=()=>{
    if(!form.sujet){showToast({type:"error",text:t.errChooseSubject});return;}
    if(!form.message||form.message.length<10){showToast({type:"error",text:t.errMessageTooShort});return;}
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if(!form.email||!emailRegex.test(form.email)){showToast({type:"error",text:t.errEmailInvalid});return;}
    setSent(true);
    showToast({type:"success",text:t.msgSentReply24h});
  };

  return (
    <div style={{
      minHeight:"100vh",
      background:"radial-gradient(ellipse 80% 60% at 50% -10%, rgba(255,180,120,.08) 0%, transparent 50%), radial-gradient(ellipse at top, #3B1F6E 0%, #1F1A4A 28%, #14132E 58%, #08081A 100%)",
      color:"#fff", display:"flex", flexDirection:"column",
      position:"relative", overflow:"hidden",
    }}>
      <G/>
      <AuthSparkles />

      {/* Header glassmorphism */}
      <div style={{
        position:"sticky", top:0, zIndex:10,
        padding:"16px 18px 14px",
        background:"rgba(13,13,26,.65)",
        backdropFilter:"blur(14px)",
        borderBottom:"1px solid rgba(255,255,255,.1)",
        display:"flex", alignItems:"center", gap:12,
      }}>
        <motion.button whileTap={{ scale: 0.92 }} onClick={onBack} style={{background:"rgba(255,255,255,.08)",border:"1px solid rgba(255,255,255,.12)",borderRadius:10,width:36,height:36,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0,color:"#fff",fontSize:18}}>←</motion.button>
        <div style={{fontFamily:"var(--fh)",fontSize:18,fontWeight:900,color:"#fff",letterSpacing:.3,flex:1}}>{t.contactTitle}</div>
      </div>

      <div style={{flex:1, padding:"16px 18px 110px", overflowY:"auto", display:"flex", flexDirection:"column", gap:12, position:"relative", zIndex:1}}>
        {sent ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1.4, 0.36, 1] }}
            style={{textAlign:"center", padding:"40px 20px"}}
          >
            <div style={{
              width:80, height:80, borderRadius:"50%",
              background:"rgba(34,197,94,.15)",
              border:"1px solid rgba(34,197,94,.35)",
              display:"flex", alignItems:"center", justifyContent:"center",
              margin:"0 auto 20px",
              boxShadow:"0 0 30px rgba(34,197,94,.3)",
            }}>
              <CIcon name="check" s={36} c="#22c55e" sw={2.5}/>
            </div>
            <div style={{fontFamily:"var(--fh)", fontSize:24, fontWeight:900, color:"#fff", marginBottom:10, letterSpacing:-.2}}>{t.messageSent}</div>
            <div style={{fontSize:13, color:"rgba(255,255,255,.7)", lineHeight:1.7, marginBottom:24}}>{t.teamWillReply}</div>
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={onBack}
              style={{
                background:"var(--app-cta-bg)",
                color:"#fff", border:"none", borderRadius:14,
                padding:"14px 32px",
                fontSize:14, fontWeight:800, fontFamily:"var(--fh)",
                letterSpacing:.5, cursor:"pointer",
                boxShadow:"0 8px 22px rgba(200,0,26,.45), inset 0 1px 0 rgba(255,255,255,.18)",
              }}
            >{t.back}</motion.button>
          </motion.div>
        ) : (
          <>
            {/* Infos contact — gradient violet/rouge */}
            <div style={{
              background:`linear-gradient(135deg, #2D1B4E 0%, #1F1A4A 100%)`,
              border:"1px solid rgba(255,255,255,.1)",
              borderRadius:14, padding:"14px 16px",
              boxShadow:"0 8px 22px rgba(0,0,0,.3)",
            }}>
              <div style={{fontFamily:"var(--fh)", fontSize:14, fontWeight:900, color:"#fff", marginBottom:10, letterSpacing:.3}}>Support HorseConnex</div>
              <div style={{display:"flex", flexDirection:"column", gap:8}}>
                {[{ic:"mail",label:"Email",val:"support@horseconnex.com"},{ic:"mail",label:t.infoGeneral,val:"info@horseconnex.com"},{ic:"phone",label:t.phone,val:"+41 00 000 00 00"},{ic:"notif",label:t.hoursLabel,val:t.hoursValue}].map(({ic,label,val})=>(
                  <div key={label} style={{display:"flex", alignItems:"center", gap:9}}>
                    <CIcon name={ic} s={14} c="#FF5A6E"/>
                    <span style={{fontSize:11, color:"rgba(255,255,255,.55)"}}>{label} :</span>
                    <span style={{fontSize:12, color:"#fff", fontWeight:600}}>{val}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Formulaire — card blanche pour la lisibilité du textarea */}
            <div style={{
              background:"#fff", borderRadius:16, padding:18,
              boxShadow:"0 12px 30px rgba(0,0,0,.4), 0 0 0 1px rgba(255,255,255,.05)",
            }}>
              <div style={{fontSize:10, fontWeight:700, color:"var(--muted)", letterSpacing:1.5, textTransform:"uppercase", marginBottom:12}}>{t.sendMessageSection}</div>
              {/* Sujet */}
              <div style={{marginBottom:14}}>
                <div style={{fontSize:10, fontWeight:700, color:"var(--muted)", letterSpacing:1.2, textTransform:"uppercase", marginBottom:7}}>{t.subject} <span style={{color:"var(--red)"}}>*</span></div>
                <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
                  {sujets.map(s=>(
                    <motion.button
                      key={s}
                      whileTap={{ scale: 0.94 }}
                      onClick={()=>up("sujet",s)}
                      style={{
                        background: form.sujet===s ? "var(--app-cta-bg)" : "var(--bg)",
                        color: form.sujet===s ? "#fff" : "var(--muted)",
                        border: `1.5px solid ${form.sujet===s ? "transparent" : "var(--border)"}`,
                        borderRadius: 20, padding:"6px 12px",
                        fontSize:11.5, fontFamily:"var(--fh)", fontWeight:700,
                        cursor:"pointer",
                        boxShadow: form.sujet===s ? "0 4px 12px rgba(200,0,26,.3)" : "none",
                        transition:"all .15s",
                      }}
                    >{s}</motion.button>
                  ))}
                </div>
              </div>
              <Field label={t.email} placeholder={t.emailPh||"votre@email.com"} type="email" icon="mail" value={form.email} onChange={e=>up("email",e.target.value)} required autoComplete="email" inputMode="email" name="email"/>
              <div style={{marginTop:12}}>
                <Field label={t.yourMessage} placeholder={t.describeIssue} rows={5} value={form.message} onChange={e=>up("message",e.target.value)} required/>
                <div style={{fontSize:10, color:"var(--muted)", textAlign:"right", marginTop:3}}>{form.message.length} {t.charactersLabel}</div>
              </div>
            </div>
          </>
        )}
      </div>
      {!sent && (
        <div style={{
          position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)",
          width:"100%", maxWidth:430,
          padding:"12px 20px 18px",
          background:"rgba(13,13,26,.85)", backdropFilter:"blur(14px)",
          borderTop:"1px solid rgba(255,255,255,.08)",
          zIndex:5,
        }}>
          <PrimaryButton onClick={handle}>{t.send}</PrimaryButton>
        </div>
      )}
    </div>
  );
}

